import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  Film,
  Search,
  Plus,
  Pencil,
  Trash2,
  HardDrive,
  X,
  Star,
  Calendar,

  User,
  Users,
  Clapperboard,
  FolderOpen,
  Power,
  Download,
  RefreshCw,
  ArrowUp,
  ArrowUpDown,
  AlertCircle,
  RotateCcw,
  Grid2x2,
  LayoutGrid,
  Grid3x3,
  Send,
  List,
  RotateCw,
  Link,
  Sun,
  Moon,
  HelpCircle,

  Loader2,
  AlertTriangle,
  Eye,
  EyeOff,
  Lock,
  Copy,
  ImageOff,
  Activity,
  Bell,
  BellOff,
  MessageSquare,
  Megaphone,
  History,
  Clock,
  ExternalLink,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent, CardHeader } from "./components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./components/ui/dialog";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import "./App.css";
import { FilmStripLoader } from "./components/FilmStripLoader";

const READONLY = process.env.REACT_APP_READONLY_MODE === "true";
// In modalità pubblica (readonly) usiamo URL relativi per passare attraverso il tunnel Cloudflare
const API_URL = READONLY ? "" : (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001");

const api = axios.create(API_URL ? { baseURL: API_URL } : {});

// Versione visibile nel pannello admin: aggiornata a ogni modifica/deploy
const APP_VERSION = "v2026-10-14.3";

// Aggiunge header per bypassare la manutenzione nel pannello admin
const isLocalhost = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
if (isLocalhost) {
  api.defaults.headers.common["X-Admin-Bypass"] = "1";
}

// Interceptor per aggiungere token password catalogo e admin (se presenti)
api.interceptors.request.use((config) => {
  try {
    const catToken = sessionStorage.getItem("catalog_token");
    if (catToken) {
      config.headers["X-Catalog-Token"] = catToken;
    }
    const admToken = sessionStorage.getItem("admin_token");
    if (admToken) {
      config.headers["X-Admin-Token"] = admToken;
    }
  } catch {}
  return config;
});

// Interceptor response: cattura 403 e notifica il componente
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      window.dispatchEvent(new CustomEvent("admin-auth-required"));
    }
    return Promise.reject(error);
  }
);

function resolveImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  if (url.startsWith("/")) {
    // In readonly (tunnel) usa URL relativi, in admin prepend il backend URL
    return READONLY ? url : `${API_URL}${url}`;
  }
  return url;
}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

function getDrive(path) {
  if (!path) return null;
  path = path.trim();

  // Windows locale: Z:\20-FILM ITALIANI\...
  if (/^[A-Za-z]:/.test(path)) {
    const parts = path.split("\\").filter(Boolean);
    for (let i = 1; i < parts.length; i++) {
      // Il drive è una cartella che:
      // - contiene "FILM", "SERIE" o "KDRIVE" (case-insensitive), OPPURE
      // - inizia con un numero MA contiene anche lettere (evita \1\, \2\, ecc.)
      if (/FILM|SERIE|KDRIVE|^\d.*[A-Za-z]/i.test(parts[i])) {
        // Se il nome contiene qualcosa tra parentesi, usa solo il contenuto
        // delle parentesi come nome drive (es. "FILM STRANIERI (KDRIVE)" -> "KDRIVE")
        const paren = parts[i].match(/\(([^)]+)\)/);
        if (paren) return paren[1];
        return parts[i];
      }
    }
    if (parts.length >= 3 && !parts[2].includes(".")) {
      return parts[2];
    }
    if (parts.length >= 2) {
      return parts[1];
    }
    return null;
  }

  // UNC path: \\Minipc\montaggio unita' emby\20-FILM ITALIANI\...
  if (path.startsWith("\\\\")) {
    const cleaned = path.replace(/^\\+/, "");
    const parts = cleaned.split("\\");
    for (let i = 0; i < parts.length; i++) {
      if (/^\d/.test(parts[i])) {
        return parts[i];
      }
    }
    if (parts.length >= 2) return `\\\\${parts[0]}\\${parts[1]}`;
    return null;
  }

  return null;
}

const CATEGORIE = [
  "FILM ITALIANI",
  "FILM STRANIERI",
  "SERIE TV STRANIERE",
  "SERIE TV ITALIANE",
  "FILM ANIMAZIONE",
  "PRIMA VISIONE",
  "CONCERTI-MUSICAL..ETC.",
  "DOCUMENTARI",
  "TEATRO",
];

const CATEGORY_COLORS = {
  "FILM ITALIANI": "#fca5a5",
  "FILM STRANIERI": "#93c5fd",
  "SERIE TV STRANIERE": "#c4b5fd",
  "SERIE TV ITALIANE": "#fdba74",
  "FILM ANIMAZIONE": "#fde047",
  "PRIMA VISIONE": "#f9a8d4",
  "CONCERTI-MUSICAL..ETC.": "#86efac",
  "DOCUMENTARI": "#EA5826",
  "TEATRO": "#fdb4d0",
};

export default function App() {
  // Imposta il titolo della scheda del browser in base alla modalità (admin da localhost, utenti dal tunnel)
  const isAdminPage = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
  useEffect(() => {
    document.title = isAdminPage ? "Catalogo Emby (Admin)" : "Catalogo Emby (utenti)";
  }, [isAdminPage]);

  const [allFilms, setAllFilms] = useState([]);
  const [displayFilms, setDisplayFilms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [localQuery, setLocalQuery] = useState("");
  const debouncedLocal = useDebounce(localQuery, 150);

  const [tmdbQuery, setTmdbQuery] = useState("");
  const debouncedTmdb = useDebounce(tmdbQuery, 500);
  const [tmdbResults, setTmdbResults] = useState([]);
  const [tmdbLoading, setTmdbLoading] = useState(false);

  const [activeAZ, setActiveAZ] = useState(null);
  const [activeDrive, setActiveDrive] = useState(null);
  const [activeCategoria, setActiveCategoria] = useState(null);
  const [activeAnno, setActiveAnno] = useState(null);
  const [activeResolution, setActiveResolution] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem("theme");
      if (saved) return saved === "dark";
      return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
      return false;
    }
  });
  const [drives, setDrives] = useState([]);
  const [tunnelUrl, setTunnelUrl] = useState(null);
  const [tunnelStatus, setTunnelStatus] = useState("loading"); // loading | ok | error

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFilm, setEditingFilm] = useState(null);
  const [form, setForm] = useState({
    titolo: "",
    anno: "",
    genere: "",
    regista: "",
    attori: "",
    descrizione: "",
    copertina_url: "",
    voto_personale: "",
    percorso_file: "",
    categoria: "",
    emby_id: "",
  });

  const [deleteId, setDeleteId] = useState(null);
  const [detailFilm, setDetailFilm] = useState(null);
  const [detailImgError, setDetailImgError] = useState(false);
  const [refreshingResolution, setRefreshingResolution] = useState(false);
  const [debugFilmData, setDebugFilmData] = useState(null);
  const [debugEmbyData, setDebugEmbyData] = useState(null);
  const [debugResolutionData, setDebugResolutionData] = useState(null);
  const [sessionClosed, setSessionClosed] = useState(false);
  const [tmdbPreview, setTmdbPreview] = useState(null);
  const [tmdbForm, setTmdbForm] = useState({ percorso_file: "", voto_personale: "", categoria: "" });
  const [confirmDuplicate, setConfirmDuplicate] = useState(null);
  const [gridCols, setGridCols] = useState(() => {
    try {
      const saved = localStorage.getItem("gridCols");
      return saved ? Number(saved) : 4;
    } catch {
      return 4;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("gridCols", gridCols);
    } catch {}
  }, [gridCols]);

  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);
  useEffect(() => {
    const checkOrientation = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsLandscapeMobile(w > h && w < 1024);
    };
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);
    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  const isLocalhost = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
  const isAdmin = isLocalhost;
  const [embyOpen, setEmbyOpen] = useState(false);
  const [embyLibraries, setEmbyLibraries] = useState([]);
  const [missingTmdbOpen, setMissingTmdbOpen] = useState(false);
  const [missingTmdbList, setMissingTmdbList] = useState([]);
  // Se true, alla chiusura della scheda film si ritorna alla lista "senza TMDB ID"
  const [returnToMissingTmdb, setReturnToMissingTmdb] = useState(false);
  // Classifica file per dimensione (admin)
  const [filesSizeOpen, setFilesSizeOpen] = useState(false);
  const [filesSizeData, setFilesSizeData] = useState(null);
  const [filesSizeLoading, setFilesSizeLoading] = useState(false);
  const [filesSizeDrive, setFilesSizeDrive] = useState("");
  const [filesSizeDir, setFilesSizeDir] = useState("desc");
  const [filesSizeCount, setFilesSizeCount] = useState(200);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [duplicateCategory, setDuplicateCategory] = useState("");
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [duplicateSearched, setDuplicateSearched] = useState(false);
  const [duplicateSort, setDuplicateSort] = useState("az");
  const [nonStandardOpen, setNonStandardOpen] = useState(false);
  const [nonStandardData, setNonStandardData] = useState(null);
  const [nonStandardLoading, setNonStandardLoading] = useState(false);
  const [nonStandardCategory, setNonStandardCategory] = useState("");
  const [nonStandardSearched, setNonStandardSearched] = useState(false);
  const [noCoverOpen, setNoCoverOpen] = useState(false);
  const [noCoverData, setNoCoverData] = useState(null);
  const [noCoverLoading, setNoCoverLoading] = useState(false);
  const [noCoverCategory, setNoCoverCategory] = useState("");
  const [noCoverSearched, setNoCoverSearched] = useState(false);
  const [activeUsersOpen, setActiveUsersOpen] = useState(false);
  const [activeUsersData, setActiveUsersData] = useState(null);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyUsers, setHistoryUsers] = useState([]);
  const [historySelectedUser, setHistorySelectedUser] = useState("");
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  const HISTORY_LIMIT = 10;
  const [usersActivity, setUsersActivity] = useState([]);
  const [usersActivityLoading, setUsersActivityLoading] = useState(false);
  const [embySelectedLibrary, setEmbySelectedLibrary] = useState("");
  const [embyImporting, setEmbyImporting] = useState(false);
  const [embyResult, setEmbyResult] = useState(null);
  const [embyDiagnosing, setEmbyDiagnosing] = useState(false);
  const [embyDiagnoseResult, setEmbyDiagnoseResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncSelectedCategoria, setSyncSelectedCategoria] = useState("");
  const [populatingActors, setPopulatingActors] = useState(false);
  const [forcePopulateActors, setForcePopulateActors] = useState(false);
  const [resettingEmptyActors, setResettingEmptyActors] = useState(false);
  const [resettingNewEpisodes, setResettingNewEpisodes] = useState(false);
  const [populatingResolution, setPopulatingResolution] = useState(false);
  const [actorsStatus, setActorsStatus] = useState(null);
  const [resolutionStatus, setResolutionStatus] = useState(null);
  const [syncingTmdbIds, setSyncingTmdbIds] = useState(false);
  const [syncTmdbStatus, setSyncTmdbStatus] = useState(null);
  const [syncingCastFromEmby, setSyncingCastFromEmby] = useState(false);
  const [fixingCastFromEmby, setFixingCastFromEmby] = useState(false);
  const [syncingTipoFromEmby, setSyncingTipoFromEmby] = useState(false);
  const [fixingCastByTmdb, setFixingCastByTmdb] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [driveSummaryOpen, setDriveSummaryOpen] = useState(false);
  const [selectedDriveSummary, setSelectedDriveSummary] = useState(null);
  const [categorySummaryOpen, setCategorySummaryOpen] = useState(false);
  const [selectedCategorySummary, setSelectedCategorySummary] = useState(null);
  const [confirmHideCategory, setConfirmHideCategory] = useState(null);
  const [driveSpaces, setDriveSpaces] = useState([]);
  const [driveSpacesLoading, setDriveSpacesLoading] = useState(false);
  const [sortBy, setSortBy] = useState("added"); // "added" | "az" | "za" | "year" | "vote"
  const latestDate = useMemo(() => {
    if (!allFilms.length) return null;
    const today = new Date().toISOString().slice(0, 10);
    const dates = allFilms
      .map((f) => f.data_aggiunta?.slice(0, 10))
      .filter(Boolean)
      .filter((d) => d <= today);
    return dates.sort().pop() || null;
  }, [allFilms]);

  const selectedActivity = useMemo(() => usersActivity.find((u) => u.id === historySelectedUser), [usersActivity, historySelectedUser]);
  const userActivityCount = selectedActivity ? selectedActivity.count : 0;
  const totalActivityCount = useMemo(() => usersActivity.reduce((sum, u) => sum + u.count, 0), [usersActivity]);
  const activityPercent = totalActivityCount > 0 ? (userActivityCount / totalActivityCount) * 100 : 0;
  const selectedUserName = selectedActivity ? selectedActivity.name : "";

  const driveSummaryData = useMemo(() => {
    if (!selectedDriveSummary) return [];
    const map = {};
    allFilms.forEach((f) => {
      if (getDrive(f.percorso_file) === selectedDriveSummary && f.categoria) {
        map[f.categoria] = (map[f.categoria] || 0) + 1;
      }
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [selectedDriveSummary, allFilms]);

  const categorySummaryData = useMemo(() => {
    if (!selectedCategorySummary) return [];
    const map = {};
    allFilms.forEach((f) => {
      if (f.categoria === selectedCategorySummary) {
        const d = getDrive(f.percorso_file) || "Sconosciuto";
        map[d] = (map[d] || 0) + 1;
      }
    });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [selectedCategorySummary, allFilms]);
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem("viewMode");
      return saved === "list" ? "list" : "grid";
    } catch {
      return "grid";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("viewMode", viewMode);
    } catch {}
  }, [viewMode]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Paginazione a "pagine intere": ogni pagina riempie esattamente un numero intero
  // di righe della griglia, così non restano mai slot vuoti, tranne nell'ultima
  // pagina quando i film sono finiti. La dimensione si adatta alle colonne effettive.
  const [visiblePages, setVisiblePages] = useState(1);
  const effectiveGridCols = isMobile ? (gridCols <= 3 ? 2 : 3) : gridCols;
  const rowsPerPage = Math.max(4, Math.ceil(24 / effectiveGridCols));
  const pageSize = effectiveGridCols * rowsPerPage;
  const visibleCount = visiblePages * pageSize;

  // Stati modulo richieste
  const [requestOpen, setRequestOpen] = useState(false);
  const requestOpenRef = useRef(false);
  useEffect(() => { requestOpenRef.current = requestOpen; }, [requestOpen]);
  const [requestForm, setRequestForm] = useState({
    nome_utente: "",
    note: "",
  });
  const [requestTipo, setRequestTipo] = useState("film"); // "film" | "serie" | "altro"
  const [requestItems, setRequestItems] = useState([{ titolo: "", anno: "" }]);
  const [requestChecks, setRequestChecks] = useState({}); // indice riga -> {exists, matches}
  const [requestCheckLoadingMap, setRequestCheckLoadingMap] = useState({}); // indice riga -> bool
  const [lastSubmittedCount, setLastSubmittedCount] = useState(1);
  const [requestNewsOpen, setRequestNewsOpen] = useState(false);
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [myRequests, setMyRequests] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [showMyRequestsDropdown, setShowMyRequestsDropdown] = useState(false);
  const [adminRequestsOpen, setAdminRequestsOpen] = useState(false);
  const [adminRequests, setAdminRequests] = useState([]);
  const [adminRequestsLoading, setAdminRequestsLoading] = useState(false);
  const [adminRequestEditing, setAdminRequestEditing] = useState(null);
  const [adminRequestUserFilter, setAdminRequestUserFilter] = useState("");
  const [adminRequestStatusFilter, setAdminRequestStatusFilter] = useState("");
  const [adminVisibleCount, setAdminVisibleCount] = useState(25); // "Carica altre": blocchi da 25
  const [adminGroupByUser, setAdminGroupByUser] = useState(() => {
    try {
      return localStorage.getItem("adminReqGroupByUser") === "1";
    } catch {
      return false;
    }
  });
  const [showAdminBackToTop, setShowAdminBackToTop] = useState(false);
  const adminRequestsScrollRef = useRef(null);
  const [adminRequestViews, setAdminRequestViews] = useState({});
  const [adminEmbyUsers, setAdminEmbyUsers] = useState([]);
  const [adminRequestViewsLoading, setAdminRequestViewsLoading] = useState(false);
  const [unseenResponses, setUnseenResponses] = useState(0);

  // Filtri (utente + stato) nella gestione richieste admin
  // Nomi raggruppati senza distinzione maiuscole/minuscole (Francesca = francesca)
  const adminRequestUsers = (() => {
    const m = new Map();
    for (const r of adminRequests) {
      const nome = (r.nome_utente || "").trim();
      if (!nome) continue;
      const key = nome.toLowerCase();
      const entry = m.get(key);
      if (entry) entry.count += 1;
      else m.set(key, { nome, count: 1 });
    }
    return [...m.values()].sort((a, b) => a.nome.localeCompare(b.nome, "it"));
  })();
  // Nomi "guest": nessuna delle loro richieste risolve a un utente Emby
  const adminGuestKeys = new Set(
    adminRequestUsers
      .map((u) => u.nome.toLowerCase())
      .filter(
        (key) =>
          !adminRequests.some(
            (r) =>
              (r.nome_utente || "").trim().toLowerCase() === key &&
              adminRequestViews[r.id]?.utente_emby
          )
      )
  );
  const adminPendingCount = adminRequests.filter((r) => r.stato === "pending" && !r.chiusa).length;
  const adminStatusCounts = {
    pending: adminRequests.filter((r) => r.stato === "pending" && !r.chiusa).length,
    approved: adminRequests.filter((r) => r.stato === "approved" && !r.chiusa).length,
    completed: adminRequests.filter((r) => r.stato === "completed" && !r.chiusa).length,
    chiuse: adminRequests.filter((r) => r.chiusa === true).length,
  };
  const adminViewCounts = {
    visto: adminRequests.filter((r) => r.stato === "completed" && adminRequestViews[r.id]?.stato === "visto").length,
    non_visto: adminRequests.filter((r) => r.stato === "completed" && adminRequestViews[r.id]?.stato !== "visto").length,
  };
  const filteredAdminRequests = adminRequests.filter((r) => {
    if (adminRequestUserFilter && (r.nome_utente || "").trim().toLowerCase() !== adminRequestUserFilter.trim().toLowerCase()) return false;
    if (adminRequestStatusFilter === "chiuse") return r.chiusa === true;
    if (adminRequestStatusFilter === "visto")
      return r.stato === "completed" && adminRequestViews[r.id]?.stato === "visto";
    if (adminRequestStatusFilter === "non_visto")
      return r.stato === "completed" && adminRequestViews[r.id]?.stato !== "visto";
    if (adminRequestStatusFilter) return r.stato === adminRequestStatusFilter && !r.chiusa;
    return true;
  });

  // "Carica altre": solo il blocco visibile viene renderizzato
  const visibleAdminRequests = filteredAdminRequests.slice(0, adminVisibleCount);

  // Vista raggruppata per utente: un blocco per nome, ordinato per data richiesta
  const GROUP_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#a855f7", "#ec4899", "#14b8a6", "#f97316", "#6366f1"];
  const adminGroupsByUser = (() => {
    const map = new Map();
    for (const r of visibleAdminRequests) {
      const key = (r.nome_utente || "").trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) map.set(key, { nome: r.nome_utente, latest: 0, items: [] });
      const g = map.get(key);
      g.items.push(r);
      const t = new Date(r.data_richiesta).getTime() || 0;
      if (t > g.latest) g.latest = t;
    }
    const groups = [...map.values()];
    for (const g of groups) {
      g.items.sort((a, b) => new Date(b.data_richiesta) - new Date(a.data_richiesta));
    }
    groups.sort((a, b) => b.latest - a.latest); // utente con la richiesta più recente in cima
    return groups;
  })();

  // Card singola richiesta (riusata in vista elenco e vista raggruppata)
  const renderAdminRequestCard = (r) => (
    <div key={r.id} className="rounded border bg-card p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1 flex-wrap">
            <div className="font-medium text-sm">{r.titolo}</div>
            {r.tipo === "serie" && (
              <Badge variant="secondary" className="text-[10px] px-1 shrink-0">📺 Serie TV</Badge>
            )}
            {r.tipo === "altro" && (
              <Badge variant="secondary" className="text-[10px] px-1 shrink-0">💬 Altro</Badge>
            )}
          </div>
          {r.anno && <span className="text-xs text-muted-foreground">({r.anno})</span>}
          <div className="text-xs text-muted-foreground mt-0.5">
            da <span className="font-medium">{r.nome_utente}</span>
            {adminRequestViews[r.id] && !adminRequestViews[r.id].utente_emby && (
              <Badge variant="secondary" className="text-[10px] ml-1 px-1 shrink-0">
                Guest
              </Badge>
            )}
            {" · "}{new Date(r.data_richiesta).toLocaleDateString("it-IT")}
          </div>
          {r.note && <div className="text-xs text-muted-foreground mt-1">{r.note}</div>}
          {r.stato === "completed" && (
            <div className="mt-1">
              {adminRequestViews[r.id]?.stato === "visto" ? (
                <span className="inline-flex items-center gap-1 rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-green-700">
                  <Eye className="h-3 w-3" />
                  Visto su Emby il {new Date(adminRequestViews[r.id].data_visione).toLocaleDateString("it-IT")}
                </span>
              ) : adminRequestViews[r.id]?.stato === "non_visto" ? (
                <span className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-700">
                  <EyeOff className="h-3 w-3" />
                  Non ancora visto su Emby
                </span>
              ) : adminRequestViews[r.id]?.stato === "utente_sconosciuto" ? (
                <span
                  className="text-amber-700"
                  title={`Chiave cercata: "${adminRequestViews[r.id]?.chiave || ""}"`}
                >
                  ⚠️ Utente «{r.nome_utente}» non trovato su Emby
                  {adminEmbyUsers.length > 0 && (
                    <span className="ml-2 inline-flex items-center gap-1">
                      <select
                        className="rounded border border-amber-300 bg-card px-2 py-0.5 text-xs"
                        value=""
                        onChange={(e) => {
                          const uid = e.target.value;
                          if (!uid) return;
                          const u = adminEmbyUsers.find((x) => x.id === uid);
                          if (!u) return;
                          saveUserMapping(r.nome_utente, u);
                        }}
                      >
                        <option value="">Associa a utente Emby...</option>
                        {adminEmbyUsers.map((u) => (
                          <option key={u.id} value={u.id}>{u.name}</option>
                        ))}
                      </select>
                    </span>
                  )}
                </span>
              ) : adminRequestViewsLoading ? (
                <span className="text-muted-foreground">Verifica visione su Emby...</span>
              ) : null}
            </div>
          )}
        </div>
        <Badge variant="secondary" className="text-[10px] shrink-0">
          {r.stato === "pending" ? "Ricevuta" : r.stato === "approved" ? "Non ancora disponibile" : r.stato === "completed" ? "Disponibile" : r.stato}
        </Badge>
      </div>
      {adminRequestEditing === r.id ? (
        <div className="space-y-2">
          <select
            className="rounded border bg-card px-2 py-1 text-sm w-full"
            value={r.stato}
            onChange={(e) => {
              const updated = adminRequests.map((x) => x.id === r.id ? { ...x, stato: e.target.value } : x);
              setAdminRequests(updated);
            }}
          >
            <option value="pending">Ricevuta</option>
            <option value="approved">Non ancora disponibile</option>
            <option value="completed">Disponibile</option>
          </select>
          <Textarea
            rows={2}
            placeholder="Risposta all'utente..."
            value={r.risposta || ""}
            onChange={(e) => {
              const updated = adminRequests.map((x) => x.id === r.id ? { ...x, risposta: e.target.value } : x);
              setAdminRequests(updated);
            }}
          />
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => updateAdminRequest(r.id, r.stato, r.risposta, false)}>Salva</Button>
            <Button size="sm" variant="outline" onClick={() => setAdminRequestEditing(null)}>Annulla</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          {r.risposta ? (
            <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded p-1.5 flex-1 mr-2">
              <span className="font-semibold">Risposta:</span> {r.risposta}
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setAdminRequestEditing(r.id)}>
              Gestisci
            </Button>
            {!r.chiusa && (
              <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => {
                if (window.confirm("Chiudere questa richiesta? Non sarà più visibile agli utenti.")) {
                  updateAdminRequest(r.id, r.stato, r.risposta, true);
                }
              }}>
                Chiudi
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-red-700 hover:text-red-800" onClick={() => {
              if (window.confirm("Eliminare definitivamente questa richiesta? L'azione è irreversibile.")) {
                deleteAdminRequest(r.id);
              }
            }}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Stati modulo segnalazioni
  const [reportOpen, setReportOpen] = useState(false);
  const [reportForm, setReportForm] = useState({ nome_utente: "", testo: "" });
  const [reportSubmitting, setReportSubmitting] = useState(false);

  // Annunci / pop-up
  const [announcementOpen, setAnnouncementOpen] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(null);
  const [adminAnnouncementOpen, setAdminAnnouncementOpen] = useState(false);
  const [adminAnnouncements, setAdminAnnouncements] = useState([]);
  const [announcementForm, setAnnouncementForm] = useState({ titolo: "", messaggio: "", attivo: true, scadenza: "" });

  // Notifiche browser native
  const [notifPermission, setNotifPermission] = useState(() => {
    try { return Notification.permission; } catch { return "default"; }
  });
  const prevUnseenRef = useRef(0);

  const [recentFilms, setRecentFilms] = useState([]);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Stati password catalogo pubblico
  const [catalogPasswordRequired, setCatalogPasswordRequired] = useState(false);
  const [catalogPasswordDialogOpen, setCatalogPasswordDialogOpen] = useState(false);
  const [catalogPasswordInput, setCatalogPasswordInput] = useState("");
  const [catalogPasswordError, setCatalogPasswordError] = useState("");
  const [catalogToken, setCatalogToken] = useState(() => {
    try { return sessionStorage.getItem("catalog_token") || ""; } catch { return ""; }
  });

  // Stati password admin
  const [adminPasswordRequired, setAdminPasswordRequired] = useState(false);
  const [adminPasswordDialogOpen, setAdminPasswordDialogOpen] = useState(false);
  const [adminPasswordInput, setAdminPasswordInput] = useState("");
  const [adminPasswordError, setAdminPasswordError] = useState("");
  const [adminToken, setAdminToken] = useState(() => {
    try { return sessionStorage.getItem("admin_token") || ""; } catch { return ""; }
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carica dati completi del film all'apertura del dialog (descrizione/attori esclusi dalla lista per performance)
  useEffect(() => {
    if (!detailFilm?.id) return;
    const fetchDetail = async () => {
      try {
        const { data } = await api.get(`/api/films/${detailFilm.id}`);
        setDetailFilm(data);
      } catch (err) {
        // fallback: lascia i dati parziali dalla lista
      }
    };
    fetchDetail();
  }, [detailFilm?.id]);

  // Applica/rimuovi classe dark e salva preferenza
  useEffect(() => {
    try {
      if (isDark) {
        document.documentElement.classList.add("dark");
        localStorage.setItem("theme", "dark");
      } else {
        document.documentElement.classList.remove("dark");
        localStorage.setItem("theme", "light");
      }
    } catch {}
  }, [isDark]);

  // Controlla se il catalogo richiede password (solo per utenti pubblici)
  useEffect(() => {
    if (isAdmin) return;
    const checkPasswordRequired = async () => {
      try {
        const { data } = await api.get("/api/auth/catalog-check");
        if (data.required) {
          const savedToken = sessionStorage.getItem("catalog_token");
          if (!savedToken) {
            setCatalogPasswordRequired(true);
            setCatalogPasswordDialogOpen(true);
          }
        }
      } catch (err) {
        // Se 401, la password è già richiesta
        if (err.response?.status === 401) {
          setCatalogPasswordRequired(true);
          setCatalogPasswordDialogOpen(true);
        }
      }
    };
    checkPasswordRequired();
  }, [isAdmin]);

  const verifyCatalogPassword = async () => {
    setCatalogPasswordError("");
    try {
      const { data } = await api.post("/api/auth/catalog-verify", { password: catalogPasswordInput });
      sessionStorage.setItem("catalog_token", data.token);
      setCatalogToken(data.token);
      setCatalogPasswordDialogOpen(false);
      setCatalogPasswordInput("");
      // Ricarica i dati
      fetchFilms();
    } catch (err) {
      setCatalogPasswordError("Password errata. Riprova.");
    }
  };

  const handleCatalogPasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      verifyCatalogPassword();
    }
  };

  // Controlla se il pannello admin richiede password (solo per admin)
  useEffect(() => {
    if (!isAdmin) return;
    const checkAdminPasswordRequired = async () => {
      try {
        const { data } = await api.get("/api/auth/admin-check");
        if (data.required) {
          const savedToken = sessionStorage.getItem("admin_token");
          if (!savedToken) {
            setAdminPasswordRequired(true);
            setAdminPasswordDialogOpen(true);
          }
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setAdminPasswordRequired(true);
          setAdminPasswordDialogOpen(true);
        }
      }
    };
    checkAdminPasswordRequired();
  }, [isAdmin]);

  // Ascolta evento 403 da interceptor axios
  useEffect(() => {
    const handler = () => {
      setAdminPasswordRequired(true);
      setAdminPasswordDialogOpen(true);
    };
    window.addEventListener("admin-auth-required", handler);
    return () => window.removeEventListener("admin-auth-required", handler);
  }, []);

  const verifyAdminPassword = async () => {
    setAdminPasswordError("");
    try {
      const { data } = await api.post("/api/auth/admin-verify", { password: adminPasswordInput });
      sessionStorage.setItem("admin_token", data.token);
      setAdminToken(data.token);
      setAdminPasswordDialogOpen(false);
      setAdminPasswordInput("");
      // Ricarica tutti i dati admin
      fetchFilms();
      fetchDrives();
      fetchHiddenCategories();
      fetchMaintenanceStatus();
    } catch (err) {
      setAdminPasswordError("Password errata. Riprova.");
    }
  };

  const handleAdminPasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      verifyAdminPassword();
    }
  };

  const fetchFilms = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setLoadError(null);
      const endpoint = isAdmin ? "/api/admin/films" : "/api/films";
      const res = await api.get(endpoint);
      setAllFilms(res.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setCatalogPasswordRequired(true);
        setCatalogPasswordDialogOpen(true);
      } else if (err.response?.status === 403) {
        setAdminPasswordRequired(true);
        setAdminPasswordDialogOpen(true);
      } else {
        setLoadError("Errore nel caricamento dei film. Riprova più tardi.");
        if (!silent) {
          toast.error("Errore nel caricamento dei film");
        }
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchAdminAnnouncements = async () => {
    try {
      const res = await api.get("/api/admin/annunci");
      setAdminAnnouncements(res.data);
    } catch {
      toast.error("Errore nel caricamento annunci");
    }
  };

  const fetchHiddenCategories = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/api/admin/hidden-categories");
      setHiddenCategories(data.categories || []);
    } catch (err) {
      console.error("Errore caricamento categorie nascoste", err);
    }
  };

  const saveHiddenCategories = async (categories) => {
    try {
      await api.post("/api/admin/hidden-categories", { categories });
      setHiddenCategories(categories);
      toast.success("Visibilità categorie aggiornata");
    } catch (err) {
      toast.error("Errore nel salvataggio");
    }
  };

  const fetchMaintenanceStatus = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/api/admin/maintenance");
      setMaintenanceMode(data.enabled);
    } catch (err) {
      console.error("Errore caricamento stato manutenzione", err);
    }
  };

  const toggleMaintenance = async () => {
    try {
      const next = !maintenanceMode;
      await api.post(`/api/admin/maintenance?enabled=${next}`);
      setMaintenanceMode(next);
      toast.success(next ? "Modalità manutenzione ATTIVATA" : "Modalità manutenzione DISATTIVATA");
    } catch (err) {
      toast.error("Errore nel cambio modalità manutenzione");
    }
  };

  useEffect(() => {
    fetchFilms();
    if (isAdmin) {
      fetchDrives();
      fetchHiddenCategories();
      fetchMaintenanceStatus();
      fetchActorsStatus();
      fetchSyncTmdbStatus();
      fetchResolutionStatus();
    }
    // eslint-disable-next-line
  }, []);

  // Polling auto-aggiornamento catalogo
  const lastAddedRef = useRef(null);
  const detailFilmRef = useRef(detailFilm);
  const dialogOpenRef = useRef(dialogOpen);
  useEffect(() => {
    // Il primo film in allFilms è il più recente (ordinato per data_aggiunta decrescente)
    lastAddedRef.current = allFilms[0]?.data_aggiunta || null;
  }, [allFilms]);
  useEffect(() => { detailFilmRef.current = detailFilm; }, [detailFilm]);
  useEffect(() => { dialogOpenRef.current = dialogOpen; }, [dialogOpen]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/api/catalog-status");
        // Confronta la data dell'ultimo film aggiunto invece del conteggio
        // (il conteggio diverge tra admin e pubblico per le categorie nascoste)
        if (data.last_added && data.last_added !== lastAddedRef.current) {
          if (!detailFilmRef.current && !dialogOpenRef.current) {
            fetchFilms(true);
          }
        }
      } catch {
        // silenzioso
      }
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carica ultimi arrivi per il catalogo pubblico
  useEffect(() => {
    if (isAdmin) return;
    const loadRecent = async () => {
      try {
        const res = await api.get("/api/films/recent?limit=10");
        setRecentFilms(res.data);
      } catch {
        // silenzioso
      }
    };
    loadRecent();
    // eslint-disable-next-line
  }, []);

  // Carica annunci attivi e mostra pop-up per quelli non ancora visti
  useEffect(() => {
    if (isAdmin) return;
    const load = async () => {
      try {
        const res = await api.get("/api/annunci");
        const active = res.data || [];
        if (active.length === 0) return;
        let seen = [];
        try {
          const raw = localStorage.getItem("seenAnnouncementIds");
          if (raw) seen = JSON.parse(raw);
        } catch {}
        const unseen = active.filter((a) => !seen.includes(a.id));
        if (unseen.length > 0) {
          setCurrentAnnouncement(unseen[0]);
          setAnnouncementOpen(true);
        }
      } catch {
        // silenzioso
      }
    };
    load();
    // eslint-disable-next-line
  }, []);

  // Check live duplicati riga per riga mentre si scrivono i titoli
  const debouncedRequestItems = useDebounce(requestItems, 400);
  useEffect(() => {
    const nonVuoti = debouncedRequestItems.some((i) => i.titolo.trim());
    if (!nonVuoti) {
      setRequestChecks({});
      setRequestCheckLoadingMap({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      const results = {};
      const loading = {};
      await Promise.all(
        debouncedRequestItems.map(async (item, idx) => {
          const t = item.titolo.trim();
          if (!t) return;
          loading[idx] = true;
          try {
            const res = await api.get(`/api/richieste/check/${encodeURIComponent(t)}`);
            if (!cancelled) results[idx] = res.data;
          } catch {
            /* verifica non bloccante */
          }
        })
      );
      if (!cancelled) {
        setRequestChecks(results);
        setRequestCheckLoadingMap(loading);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedRequestItems]);

  // Popup "novità modulo" all'apertura (con opzione "non mostrare più")
  useEffect(() => {
    if (!requestOpen) return;
    let hidden = false;
    try {
      hidden = localStorage.getItem("richiesteNovitaNascosta") === "1";
    } catch {}
    if (!hidden) setRequestNewsOpen(true);
  }, [requestOpen]);

  const closeRequestNews = (dontShowAgain) => {
    if (dontShowAgain) {
      try {
        localStorage.setItem("richiesteNovitaNascosta", "1");
      } catch {}
    }
    setRequestNewsOpen(false);
  };

  const TIPO_RICHIESTA_META = {
    film: { label: "Film", icon: "🎬", fieldLabel: "Titolo del film *", placeholder: "Titolo del film" },
    serie: { label: "Serie TV", icon: "📺", fieldLabel: "Titolo della serie *", placeholder: "Titolo della serie" },
    altro: { label: "Altro", icon: "💬", fieldLabel: "Cosa stai cercando? *", placeholder: "Descrivi brevemente (es. documentario, concerto...)" },
  };

  const addRequestItem = () =>
    setRequestItems((items) =>
      items.length >= 10 ? items : [...items, { titolo: "", anno: "" }]
    );

  const removeRequestItem = (idx) =>
    setRequestItems((items) =>
      items.length <= 1 ? items : items.filter((_, i) => i !== idx)
    );

  const updateRequestItem = (idx, field, value) =>
    setRequestItems((items) =>
      items.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );

  const validRequestCount = requestItems.filter((i) => i.titolo.trim()).length;

  const fetchMyRequests = async () => {
    try {
      const savedIds = JSON.parse(localStorage.getItem("myRequestIds") || "[]");
      if (!savedIds.length) {
        setMyRequests([]);
        return;
      }
      setMyRequestsLoading(true);
      const { data } = await api.get(`/api/richieste/mie?ids=${savedIds.join(",")}`);
      setMyRequests(data);
    } catch {
      setMyRequests([]);
    } finally {
      setMyRequestsLoading(false);
    }
  };

  useEffect(() => {
    if (requestOpen) {
      fetchMyRequests();
      if (unseenResponses > 0) {
        markResponsesSeen();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestOpen]);

  // Polling notifiche risposte admin (solo per utenti pubblici)
  useEffect(() => {
    if (isAdmin) return;
    checkUnseenResponses();
    const interval = setInterval(() => {
      checkUnseenResponses();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);

  const submitRequest = async () => {
    const validItems = requestItems.filter((i) => i.titolo.trim());
    if (!requestForm.nome_utente.trim() || validItems.length === 0) {
      toast.error("Inserisci il tuo nome e almeno un titolo");
      return;
    }
    setRequestSubmitting(true);
    try {
      const { data } = await api.post("/api/richieste/batch", {
        nome_utente: requestForm.nome_utente.trim(),
        tipo: requestTipo,
        note: requestForm.note?.trim() || null,
        elementi: validItems.map((i) => ({
          titolo: i.titolo.trim(),
          anno: i.anno ? parseInt(i.anno, 10) || null : null,
        })),
      });
      const newIds = (data || []).map((r) => r.id);
      const savedIds = JSON.parse(localStorage.getItem("myRequestIds") || "[]");
      localStorage.setItem("myRequestIds", JSON.stringify([...savedIds, ...newIds]));
      toast.success(newIds.length > 1 ? `${newIds.length} richieste inviate!` : "Richiesta inviata!");
      setLastSubmittedCount(newIds.length || 1);
      setRequestItems([{ titolo: "", anno: "" }]);
      setRequestChecks({});
      setRequestForm((f) => ({ ...f, note: "" })); // il nome resta, comodo per nuovi invii
      setRequestSuccess(true);
      fetchMyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio della richiesta");
    } finally {
      setRequestSubmitting(false);
    }
  };

  const fetchAdminRequests = async (silent = false) => {
    try {
      if (!silent) setAdminRequestsLoading(true);
      const { data } = await api.get("/api/richieste");
      setAdminRequests(data);
    } catch {
      if (!silent) toast.error("Errore nel caricamento richieste");
    } finally {
      setAdminRequestsLoading(false);
    }
  };

  // Carica il conteggio richieste per l'admin appena entra (per il badge sul pulsante)
  useEffect(() => {
    if (!isAdmin) return;
    fetchAdminRequests(true);
    const interval = setInterval(() => fetchAdminRequests(true), 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchRequestViews = async () => {
    try {
      setAdminRequestViewsLoading(true);
      const { data } = await api.get("/api/admin/richieste/visioni");
      setAdminRequestViews(data?.mappa || {});
      setAdminEmbyUsers(data?.utenti_emby || []);
    } catch {
      // silenzioso: la nota visione resta semplicemente assente
    } finally {
      setAdminRequestViewsLoading(false);
    }
  };

  const saveUserMapping = async (nomeUtente, embyUser) => {
    try {
      await api.post("/api/admin/richieste/mappa", {
        nome_utente: nomeUtente,
        emby_user_id: embyUser.id,
        emby_user_name: embyUser.name,
      });
      toast.success(`«${nomeUtente}» associato a «${embyUser.name}»`);
      fetchRequestViews();
    } catch {
      toast.error("Errore nel salvare l'associazione");
    }
  };

  const restoreAdminScroll = () => {
    const el = adminRequestsScrollRef.current;
    if (!el) return;
    const savedTop = el.scrollTop;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (adminRequestsScrollRef.current) {
          adminRequestsScrollRef.current.scrollTop = savedTop;
        }
      });
    });
  };

  const updateAdminRequest = async (id, stato, risposta, chiusa) => {
    try {
      await api.patch(`/api/admin/richieste/${id}`, { stato, risposta, chiusa });
      toast.success("Richiesta aggiornata");
      await fetchAdminRequests(true);
      fetchRequestViews();
      restoreAdminScroll();
      if (chiusa) {
        fetchMyRequests();
      }
      setAdminRequestEditing(null);
    } catch {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const deleteAdminRequest = async (id) => {
    try {
      await api.delete(`/api/richieste/${id}`);
      toast.success("Richiesta eliminata");
      await fetchAdminRequests(true);
      fetchRequestViews();
      restoreAdminScroll();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const checkUnseenResponses = async () => {
    try {
      const savedIds = JSON.parse(localStorage.getItem("myRequestIds") || "[]");
      if (!savedIds.length) {
        setUnseenResponses(0);
        prevUnseenRef.current = 0;
        return;
      }
      const { data } = await api.get(`/api/richieste/nuove-risposte?ids=${savedIds.join(",")}`);
      const newCount = data.count || 0;
      // Se il numero di risposte non viste è aumentato, notifica l'utente
      // (solo se il dialog richieste non è aperto, per non spammare)
      if (newCount > prevUnseenRef.current && newCount > 0 && !requestOpenRef.current) {
        showBrowserNotification(newCount);
        playNotificationSound();
      }
      prevUnseenRef.current = newCount;
      setUnseenResponses(newCount);
    } catch {
      setUnseenResponses(0);
      prevUnseenRef.current = 0;
    }
  };

  const markResponsesSeen = async () => {
    try {
      const savedIds = JSON.parse(localStorage.getItem("myRequestIds") || "[]");
      if (!savedIds.length) return;
      await api.post(`/api/richieste/segna-viste?ids=${savedIds.join(",")}`);
      setUnseenResponses(0);
      prevUnseenRef.current = 0;
    } catch {
      // silenzioso
    }
  };

  // ── Notifiche browser native ──
  function playNotificationSound() {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    } catch {}
  }

  function showBrowserNotification(count) {
    if (!("Notification" in window)) return;
    if (Notification.permission !== "granted") return;
    try {
      new Notification("Nuova risposta dal catalogo film", {
        body: count === 1 ? "Hai una nuova risposta dall'amministratore." : `Hai ${count} nuove risposte dall'amministratore.`,
        tag: "catalogo-risposta",
        requireInteraction: false,
      });
    } catch {}
  }

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      toast.error("Il tuo browser non supporta le notifiche.");
      return;
    }
    const result = await Notification.requestPermission();
    setNotifPermission(result);
    if (result === "granted") {
      toast.success("Notifiche abilitate! Riceverai un avviso quando rispondiamo.");
    } else if (result === "denied") {
      toast.error("Hai bloccato le notifiche. Puoi riabilitarle dalle impostazioni del browser.");
    }
  };

  const submitReport = async () => {
    if (!reportForm.nome_utente.trim() || !reportForm.testo.trim()) {
      toast.error("Nome e descrizione sono obbligatori");
      return;
    }
    setReportSubmitting(true);
    try {
      await api.post("/api/segnalazioni", {
        nome_utente: reportForm.nome_utente.trim(),
        testo: reportForm.testo.trim(),
      });
      toast.success("Segnalazione inviata!");
      setReportForm({ nome_utente: "", testo: "" });
      setReportOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'invio della segnalazione");
    } finally {
      setReportSubmitting(false);
    }
  };

  // In modalità pubblica, mostra l'URL del dominio fisso
  useEffect(() => {
    if (isAdmin) return;
    // Con dominio fisso (catalogoemby.me), l'URL è sempre quello corrente
    if (window.location.hostname !== "127.0.0.1" && window.location.hostname !== "localhost") {
      setTunnelUrl(window.location.origin);
      setTunnelStatus("ok");
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Reset paginazione quando cambiano filtri o ricerca
  useEffect(() => {
    setVisiblePages(1);
  }, [debouncedLocal, activeAZ, activeDrive, activeCategoria, activeAnno, activeResolution, sortBy]);

  // Reset anno quando cambia categoria
  useEffect(() => {
    setActiveAnno(null);
  }, [activeCategoria]);

  const fetchDrives = async () => {
    try {
      const res = await api.get("/api/films/drives");
      setDrives(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchDriveSpaces = async () => {
    try {
      setDriveSpacesLoading(true);
      const res = await api.get("/api/admin/drive-spaces");
      setDriveSpaces(res.data);
    } catch (err) {
      console.error(err);
      setDriveSpaces([]);
    } finally {
      setDriveSpacesLoading(false);
    }
  };

  const fetchFilesBySize = async (fresh = false) => {
    try {
      setFilesSizeLoading(true);
      const res = await api.get("/api/admin/files-by-size", {
        params: fresh ? { fresh: 1 } : {},
      });
      setFilesSizeData(res.data);
      setFilesSizeCount(200);
      setFilesSizeDrive("");
    } catch (err) {
      console.error(err);
      toast.error("Errore caricamento dimensioni file");
    } finally {
      setFilesSizeLoading(false);
    }
  };

  const formatGB = (bytes) =>
    ((bytes || 0) / 1e9).toFixed(1).replace(".", ",") + " GB";

  const exportFilesSizeTxt = () => {
    if (!filesSizeData || filesSizeFiltered.length === 0) return;
    const lines = [];
    lines.push("EMBY DB APP - File per dimensione");
    lines.push(`Generato il ${new Date().toLocaleString("it-IT")}`);
    lines.push(`Unita': ${filesSizeDrive || "Tutte"}   Ordine: ${filesSizeDir === "desc" ? "dalla piu' pesante alla piu' leggera" : "dalla piu' leggera alla piu' pesante"}`);
    lines.push(`File elencati: ${filesSizeFiltered.length}   Totale occupato: ${formatGB(filesSizeTotalBytes)}`);
    lines.push("");
    filesSizeFiltered.forEach((f, idx) => {
      const ris = f.risoluzione ? ` [${f.risoluzione}]` : "";
      lines.push(
        `${String(idx + 1).padStart(4)}. ${formatGB(f.size_bytes).padStart(9)}${ris}  ${f.titolo}  (${f.drive || "?"} - ${f.categoria})`
      );
      lines.push(`      ${f.percorso_file}`);
    });
    const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `file-per-dimensione_${filesSizeDrive || "tutte"}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const filesSizeFiltered = useMemo(() => {
    if (!filesSizeData) return [];
    const list = filesSizeDrive
      ? filesSizeData.files.filter((f) => f.drive === filesSizeDrive)
      : filesSizeData.files;
    return [...list].sort((a, b) =>
      filesSizeDir === "desc" ? b.size_bytes - a.size_bytes : a.size_bytes - b.size_bytes
    );
  }, [filesSizeData, filesSizeDrive, filesSizeDir]);
  const filesSizeTotalBytes = filesSizeFiltered.reduce((s, f) => s + f.size_bytes, 0);

  // handleShutdown rimosso per sicurezza: non esporre il pulsante di arresto nel frontend pubblico

  useEffect(() => {
    if (!debouncedLocal.trim()) {
      setSearchResults([]);
      applyClientFilters(allFilms);
      return;
    }
    api
      .get(`/api/films/search/local/${encodeURIComponent(debouncedLocal)}`)
      .then((res) => {
        setSearchResults(res.data);
        applyClientFilters(res.data, true);
      })
      .catch(() => toast.error("Errore nella ricerca"));
    // eslint-disable-next-line
  }, [debouncedLocal, allFilms]);

  useEffect(() => {
    if (driveSummaryOpen || categorySummaryOpen) {
      fetchDriveSpaces();
    }
    // eslint-disable-next-line
  }, [driveSummaryOpen, categorySummaryOpen]);

  useEffect(() => {
    if (!debouncedTmdb.trim() || !isAdmin) {
      setTmdbResults([]);
      return;
    }
    setTmdbLoading(true);
    api
      .get(`/api/movies/search/${encodeURIComponent(debouncedTmdb)}`)
      .then((res) => setTmdbResults(res.data))
      .catch(() => toast.error("Errore ricerca TMDB"))
      .finally(() => setTmdbLoading(false));
  }, [debouncedTmdb]);

  // Scrolla in cima quando appaiono risultati TMDB
  useEffect(() => {
    if (tmdbResults.length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [tmdbResults]);

  function applyClientFilters(baseList, skipCatAnno = false) {
    let list = [...baseList];

    if (activeAZ && activeAZ !== "0-9") {
      list = list.filter((f) => f.titolo?.toUpperCase().startsWith(activeAZ));
    } else if (activeAZ === "0-9") {
      list = list.filter((f) => /^[0-9]/.test(f.titolo));
    }
    if (activeDrive) {
      list = list.filter((f) => getDrive(f.percorso_file) === activeDrive);
    }
    if (!skipCatAnno && activeCategoria) {
      list = list.filter((f) => f.categoria === activeCategoria);
    }
    if (!skipCatAnno && activeAnno) {
      list = list.filter((f) => f.anno === activeAnno);
    }
    if (activeResolution) {
      list = list.filter((f) => f.risoluzione === activeResolution);
    }
    if (sortBy === "az") {
      list.sort((a, b) => a.titolo.localeCompare(b.titolo, "it", { sensitivity: "base" }));
    } else if (sortBy === "za") {
      list.sort((a, b) => b.titolo.localeCompare(a.titolo, "it", { sensitivity: "base" }));
    } else if (sortBy === "year") {
      list.sort((a, b) => (b.anno || 0) - (a.anno || 0));
    } else if (sortBy === "vote") {
      list.sort((a, b) => (b.voto_personale || 0) - (a.voto_personale || 0));
    } else {
      list.sort((a, b) => {
        const da = a.data_aggiunta ? Date.parse(a.data_aggiunta) : 0;
        const db = b.data_aggiunta ? Date.parse(b.data_aggiunta) : 0;
        return db - da;
      });
    }
    setDisplayFilms(list);
  }

  useEffect(() => {
    if (debouncedLocal.trim()) {
      applyClientFilters(searchResults, true);
    } else {
      applyClientFilters(allFilms);
    }
    // eslint-disable-next-line
  }, [activeAZ, activeDrive, activeCategoria, activeAnno, activeResolution, allFilms, sortBy, searchResults]);

  const addFromTmdb = async (tmdbId, mediaType = "movie") => {
    try {
      setTmdbLoading(true);
      const res = await api.get(`/api/movies/details/${tmdbId}?type=${mediaType}`);
      setTmdbPreview({ ...res.data, tipo: mediaType === "tv" ? "Series" : "Movie" });
      setTmdbForm({ percorso_file: "", voto_personale: "", categoria: "" });
    } catch (err) {
      toast.error("Errore nel recupero dettagli TMDB");
    } finally {
      setTmdbLoading(false);
    }
  };

  const searchByActor = async (actorName) => {
    if (!actorName || !actorName.trim()) return;
    const name = actorName.trim();
    setLocalQuery(name);
    setDetailFilm(null);
    setReturnToMissingTmdb(false);
    setDebugFilmData(null);
    setDebugEmbyData(null);
    setDebugResolutionData(null);
    setTmdbQuery("");
    setTmdbResults([]);
    setActiveAZ(null);
    setActiveDrive(null);
    try {
      const res = await api.get(`/api/films/search/actor/${encodeURIComponent(name)}`);
      setSearchResults(res.data);
      applyClientFilters(res.data, true);
      toast.info(`Trovati ${res.data.length} contenuti per "${name}"`);
    } catch (err) {
      toast.error("Errore nella ricerca per attore");
    }
  };

  const saveTmdbPreview = async () => {
    if (!tmdbForm.categoria) {
      toast.error("Seleziona una categoria");
      return;
    }
    if (!tmdbPreview) return;
    const payload = {
      ...tmdbPreview,
      percorso_file: tmdbForm.percorso_file?.trim() || null,
      voto_personale: tmdbForm.voto_personale ? parseFloat(tmdbForm.voto_personale) || null : null,
      categoria: tmdbForm.categoria,
    };
    try {
      await api.post("/api/films", payload);
      toast.success("Film aggiunto!");
      setTmdbPreview(null);
      setTmdbQuery("");
      setTmdbResults([]);
      fetchFilms();
      fetchDrives();
      resetToLatest();
    } catch (err) {
      if (err.response?.status === 409) {
        setTmdbPreview(null);
        setConfirmDuplicate(payload);
      } else {
        toast.error("Errore nel salvataggio");
      }
    }
  };

  const forceSave = async (payload) => {
    try {
      await api.post("/api/films?force=true", payload);
      toast.success("Film aggiunto!");
      setConfirmDuplicate(null);
      setDialogOpen(false);
      setEditingFilm(null);
      clearForm();
      setTmdbPreview(null);
      setTmdbQuery("");
      setTmdbResults([]);
      fetchFilms();
      fetchDrives();
      resetToLatest();
    } catch (err) {
      toast.error("Errore nel salvataggio forzato");
    }
  };

  const markEpisodesSeen = async (filmId) => {
    try {
      await api.post(`/api/films/${filmId}/mark-episodes-seen`);
      toast.success("Episodi segnati come visti");
      setDetailFilm((prev) => (prev ? { ...prev, has_new_episodes: false } : null));
      fetchFilms();
    } catch (err) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const resetAllNewEpisodes = async () => {
    if (!window.confirm("Sei sicuro di voler resettare tutti i badge 'Nuovi Episodi' per tutte le serie TV?")) return;
    try {
      setResettingNewEpisodes(true);
      const { data } = await api.post("/api/admin/reset-new-episodes");
      toast.success(`Reset completato: ${data.reset} serie aggiornate`);
      fetchFilms();
    } catch (err) {
      toast.error("Errore nel reset dei badge");
    } finally {
      setResettingNewEpisodes(false);
    }
  };

  const fetchEmbyLibraries = async () => {
    try {
      const { data } = await api.get("/api/emby/libraries");
      const filtered = (data.libraries || []).filter((lib) => !lib.name.toLowerCase().includes("xxx"));
      setEmbyLibraries(filtered);
      if (filtered.length > 0) {
        setEmbySelectedLibrary(filtered[0].id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore connessione Emby");
    }
  };

  const runEmbyDiagnose = async () => {
    setEmbyDiagnosing(true);
    setEmbyDiagnoseResult(null);
    try {
      const { data } = await api.get("/api/emby/diagnose");
      setEmbyDiagnoseResult(data);
      if (data.ok) {
        toast.success("Connessione Emby OK!");
      } else {
        toast.error(data.error || "Errore connessione Emby");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante la diagnostica");
    } finally {
      setEmbyDiagnosing(false);
    }
  };

  const handleEmbyImport = async () => {
    if (!embySelectedLibrary) {
      toast.error("Seleziona una libreria Emby");
      return;
    }
    setEmbyImporting(true);
    setEmbyResult(null);
    try {
      const { data } = await api.post("/api/emby/import", {
        library_id: embySelectedLibrary,
        target_categoria: embySelectedCategoria,
      });
      setEmbyResult(data);
      toast.success(`Importati ${data.imported} film, saltati ${data.skipped}`);
      fetchFilms();
      fetchDrives();
      resetToLatest();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore importazione");
    } finally {
      setEmbyImporting(false);
    }
  };

  const openEmby = () => {
    setEmbyOpen(true);
    setEmbyResult(null);
    fetchEmbyLibraries();
  };

  // La categoria di destinazione è implicita: corrisponde al nome della libreria Emby scelta
  const embySelectedCategoria = useMemo(() => {
    if (!embySelectedLibrary) return "";
    const selectedLib = embyLibraries.find((lib) => lib.id === embySelectedLibrary);
    return selectedLib?.name || "";
  }, [embySelectedLibrary, embyLibraries]);

  // Auto-refresh utenti attivi ogni 30 secondi quando il dialog è aperto
  useEffect(() => {
    if (!activeUsersOpen) return;
    fetchActiveUsers();
    const interval = setInterval(() => {
      fetchActiveUsers();
    }, 30000);
    return () => clearInterval(interval);
  }, [activeUsersOpen]);

  const fetchActorsStatus = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/api/admin/actors-status");
      setActorsStatus(data);
    } catch (err) {
      console.error("Errore caricamento stato attori", err);
    }
  };

  const fetchResolutionStatus = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/api/admin/resolution-status");
      setResolutionStatus(data);
    } catch (err) {
      console.error("Errore caricamento stato risoluzioni", err);
    }
  };

  const runPopulateActors = async () => {
    if (populatingActors) return;
    setPopulatingActors(true);
    let totalUpdated = 0;

    try {
      while (true) {
        const forceParam = forcePopulateActors ? "&force=1" : "";
        const { data } = await api.get(`/api/admin/populate-actors?limit=300${forceParam}`);
        totalUpdated += data.updated;

        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Aggiornati ${totalUpdated} film totali.`);
          } else {
            toast.info("Tutti i film hanno già gli attori.");
          }
          break;
        }

        toast.info(`Batch: +${data.updated} film (${totalUpdated} totali, ${data.failed} falliti)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel popolamento attori");
    } finally {
      setPopulatingActors(false);
      fetchActorsStatus();
      fetchFilms();
    }
  };

  const runResetEmptyActors = async () => {
    if (resettingEmptyActors) return;
    setResettingEmptyActors(true);
    try {
      const { data } = await api.post("/api/admin/reset-empty-actors");
      toast.success(`Resettati ${data.reset} film con attori vuoti. Ora puoi ri-processarli.`);
      fetchActorsStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore reset attori vuoti");
    } finally {
      setResettingEmptyActors(false);
    }
  };

  const fetchSyncTmdbStatus = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/api/admin/sync-tmdb-ids-status");
      setSyncTmdbStatus(data);
    } catch (err) {
      console.error("Errore caricamento stato sync tmdb ids", err);
    }
  };

  const fetchDuplicates = async (cat = duplicateCategory) => {
    setDuplicateLoading(true);
    try {
      const catParam = cat ? `?categoria=${encodeURIComponent(cat)}` : "";
      const { data } = await api.get(`/api/admin/duplicates${catParam}`);
      setDuplicateData(data);
    } catch (err) {
      toast.error("Errore nel caricamento duplicati");
    } finally {
      setDuplicateLoading(false);
    }
  };

  const fetchActiveUsers = async () => {
    setActiveUsersLoading(true);
    try {
      const { data } = await api.get("/api/admin/active-users");
      setActiveUsersData(data);
    } catch (err) {
      toast.error("Errore nel caricamento utenti attivi");
    } finally {
      setActiveUsersLoading(false);
    }
  };

  const fetchHistoryUsers = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/api/admin/emby-users");
      setHistoryUsers(data || []);
    } catch (err) {
      toast.error("Errore nel caricamento utenti Emby");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchHistoryData = async (offset = 0, userId = null) => {
    const uid = userId || historySelectedUser;
    if (!uid) return;
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/api/admin/user-history/${encodeURIComponent(uid)}?limit=${HISTORY_LIMIT}&offset=${offset}`);
      setHistoryData(data);
      setHistoryOffset(offset);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento storico");
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchUsersActivity = async () => {
    setUsersActivityLoading(true);
    try {
      const { data } = await api.get("/api/admin/users-activity");
      setUsersActivity(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento attività utenti");
    } finally {
      setUsersActivityLoading(false);
    }
  };

  const openHistoryDialog = () => {
    setHistoryOpen(true);
    setHistorySelectedUser("");
    setHistoryData(null);
    setHistoryOffset(0);
    setUsersActivity([]);
    fetchHistoryUsers();
    fetchUsersActivity();
  };

  const runSyncTmdbIds = async () => {
    if (syncingTmdbIds) return;
    setSyncingTmdbIds(true);
    let totalUpdated = 0;
    try {
      while (true) {
        const { data } = await api.post("/api/admin/sync-tmdb-ids?limit=300");
        totalUpdated += data.updated;
        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Sincronizzati ${totalUpdated} tmdb_id totali.`);
          } else {
            toast.info("Nessun tmdb_id mancante da sincronizzare.");
          }
          break;
        }
        if (data.updated === 0) {
          toast.warning(`Nessun tmdb_id trovato su Emby per ${data.total} film. Controlla i log del backend o usa Debug Emby su una card.`);
          break;
        }
        toast.info(`Batch sync: +${data.updated} tmdb_id (${totalUpdated} totali)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella sincronizzazione tmdb_id");
    } finally {
      setSyncingTmdbIds(false);
      fetchSyncTmdbStatus();
      fetchFilms();
    }
  };

  const runSyncCastFromEmby = async () => {
    if (syncingCastFromEmby) return;
    setSyncingCastFromEmby(true);
    let totalUpdated = 0;
    try {
      while (true) {
        const { data } = await api.post("/api/admin/sync-cast-from-emby?limit=300");
        totalUpdated += data.updated;
        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Sincronizzati ${totalUpdated} cast da Emby.`);
          } else {
            toast.info("Nessun cast mancante da sincronizzare da Emby.");
          }
          break;
        }
        if (data.updated === 0) {
          toast.warning(`Nessun cast trovato su Emby per ${data.total} film. Controlla che i film esistano ancora su Emby.`);
          break;
        }
        toast.info(`Batch cast Emby: +${data.updated} film (${totalUpdated} totali)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella sincronizzazione cast da Emby");
    } finally {
      setSyncingCastFromEmby(false);
      fetchActorsStatus();
      fetchFilms();
    }
  };

  const runFixCastFromEmby = async () => {
    if (fixingCastFromEmby) return;
    setFixingCastFromEmby(true);
    let totalUpdated = 0;
    try {
      while (true) {
        const { data } = await api.post("/api/admin/sync-cast-from-emby?limit=300&force=true");
        totalUpdated += data.updated;
        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Corretti ${totalUpdated} cast da Emby.`);
          } else {
            toast.info("Nessun cast da correggere.");
          }
          break;
        }
        if (data.updated === 0) {
          toast.warning(`Nessun cast trovato su Emby per ${data.total} film.`);
          break;
        }
        toast.info(`Batch correzione cast Emby: +${data.updated} film (${totalUpdated} totali)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella correzione cast da Emby");
    } finally {
      setFixingCastFromEmby(false);
      fetchActorsStatus();
      fetchFilms();
    }
  };

  const runSyncTipoFromEmby = async () => {
    if (syncingTipoFromEmby) return;
    setSyncingTipoFromEmby(true);
    let totalUpdated = 0;
    try {
      while (true) {
        const { data } = await api.post("/api/admin/sync-tipo-from-emby?limit=300");
        totalUpdated += data.updated;
        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Aggiornati ${totalUpdated} tipi da Emby.`);
          } else {
            toast.info("Tutti i film hanno già il tipo.");
          }
          break;
        }
        if (data.updated === 0) {
          toast.warning(`Nessun tipo trovato su Emby per ${data.total} film.`);
          break;
        }
        toast.info(`Batch tipo Emby: +${data.updated} film (${totalUpdated} totali)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella sincronizzazione tipo da Emby");
    } finally {
      setSyncingTipoFromEmby(false);
      fetchFilms();
    }
  };

  const runFixCastByTmdb = async () => {
    if (fixingCastByTmdb) return;
    setFixingCastByTmdb(true);
    let totalUpdated = 0;
    try {
      while (true) {
        const { data } = await api.post("/api/admin/fix-cast-by-tmdb?limit=300&force=true");
        totalUpdated += data.updated;
        if (data.total === 0) {
          if (totalUpdated > 0) {
            toast.success(`Completato! Corretti ${totalUpdated} cast da TMDB.`);
          } else {
            toast.info("Nessun cast da correggere.");
          }
          break;
        }
        if (data.updated === 0) {
          toast.warning(`Nessun cast aggiornato su ${data.total} film. Controlla i log del backend.`);
          break;
        }
        toast.info(`Batch correzione TMDB: +${data.updated} film (${totalUpdated} totali)...`);
        await new Promise((r) => setTimeout(r, 800));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella correzione cast da TMDB");
    } finally {
      setFixingCastByTmdb(false);
      fetchActorsStatus();
      fetchFilms();
    }
  };

  const runPopulateResolution = async () => {
    if (populatingResolution) return;
    setPopulatingResolution(true);
    try {
      const { data } = await api.post("/api/admin/populate-resolution");
      if (data.updated > 0) {
        toast.success(`Aggiornate ${data.updated} risoluzioni (${data.unchanged} già ok, ${data.not_found} non trovati su Emby).`);
      } else if (data.total > 0) {
        toast.info(`Nessuna risoluzione aggiornata: ${data.unchanged} già corrette, ${data.not_found} non trovati su Emby.`);
      } else {
        toast.warning("Nessuna risoluzione trovata su Emby. Verifica la connessione.");
      }
      fetchResolutionStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel popolamento risoluzioni");
    } finally {
      setPopulatingResolution(false);
      fetchFilms();
    }
  };

  const syncLibrary = async (categoria = null, silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const catParam = categoria ? `?categoria=${encodeURIComponent(categoria)}` : "";
      const { data } = await api.post(`/api/sync${catParam}`);
      let msgs = [];
      if (data.removed > 0) msgs.push(`${data.removed} film rimosso/i`);
      if (data.updated > 0) msgs.push(`${data.updated} film aggiornato/i`);

      if (msgs.length > 0) {
        toast.success(msgs.join(", "));
      } else if (!silent) {
        toast.info("Libreria già aggiornata");
      }
      setSyncOpen(false);
      fetchFilms();
      if (isAdmin) fetchDrives();
    } catch (err) {
      if (!silent) {
        toast.error(err.response?.data?.detail || "Errore sincronizzazione");
      }
    } finally {
      setSyncing(false);
    }
  };

  const openSyncDialog = () => {
    setSyncSelectedCategoria(activeCategoria || "");
    setSyncOpen(true);
  };

  const openAdd = () => {
    setEditingFilm(null);
    setForm({
      titolo: "",
      anno: "",
      genere: "",
      regista: "",
      attori: "",
      descrizione: "",
      copertina_url: "",
      voto_personale: "",
      percorso_file: "",
      categoria: "",
      emby_id: "",
    });
    setDialogOpen(true);
  };

  const openEdit = (film) => {
    setEditingFilm(film);
    setForm({
      titolo: film.titolo || "",
      anno: film.anno ?? "",
      genere: film.genere || "",
      regista: film.regista || "",
      attori: film.attori || "",
      descrizione: film.descrizione || "",
      copertina_url: film.copertina_url || "",
      voto_personale: film.voto_personale ?? "",
      percorso_file: film.percorso_file || "",
      categoria: film.categoria || "",
      emby_id: film.emby_id || "",
    });
    setDialogOpen(true);
  };

  const clearForm = () => {
    setForm({
      titolo: "",
      anno: "",
      genere: "",
      regista: "",
      attori: "",
      descrizione: "",
      copertina_url: "",
      voto_personale: "",
      percorso_file: "",
      categoria: "",
    });
  };

  const resetToLatest = () => {
    setSortBy("added");
    setActiveAZ(null);
    setActiveDrive(null);
    setActiveCategoria(null);
    setActiveAnno(null);
    setActiveResolution(null);
    setLocalQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveFilm = async () => {
    if (!form.titolo.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    if (!form.categoria) {
      toast.error("La categoria è obbligatoria");
      return;
    }
    const payload = {
      ...form,
      anno: form.anno ? parseInt(form.anno, 10) || null : null,
      voto_personale: form.voto_personale
        ? parseFloat(form.voto_personale) || null
        : null,
    };
    try {
      if (editingFilm) {
        await api.put(`/api/films/${editingFilm.id}`, payload);
        toast.success("Film aggiornato!");
        setDialogOpen(false);
        setEditingFilm(null);
        clearForm();
        fetchFilms();
        fetchDrives();
      } else {
        await api.post("/api/films", payload);
        toast.success("Film aggiunto!");
        setDialogOpen(false);
        setEditingFilm(null);
        clearForm();
        fetchFilms();
        fetchDrives();
        resetToLatest();
      }
    } catch (err) {
      if (!editingFilm && err.response?.status === 409) {
        setDialogOpen(false);
        setConfirmDuplicate(payload);
      } else {
        toast.error("Errore nel salvataggio");
      }
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/api/films/${deleteId}`);
      toast.success("Film eliminato");
      setDeleteId(null);
      fetchFilms();
      fetchDrives();
    } catch (err) {
      toast.error("Errore eliminazione");
    }
  };

  const letters = useMemo(() => {
    const arr = [];
    for (let i = 65; i <= 90; i++) arr.push(String.fromCharCode(i));
    return arr;
  }, []);

  const categoryCounts = useMemo(() => {
    const counts = {};
    CATEGORIE.forEach((cat) => {
      counts[cat] = allFilms.filter((f) => f.categoria === cat).length;
    });
    return { total: allFilms.length, ...counts };
  }, [allFilms]);

  const availableYears = useMemo(() => {
    const source = activeCategoria
      ? allFilms.filter((f) => f.categoria === activeCategoria)
      : allFilms;
    const years = new Set();
    source.forEach((f) => {
      if (f.anno) years.add(f.anno);
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [allFilms, activeCategoria]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Dialog password admin */}
      {adminPasswordDialogOpen && isAdmin && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-[#0f172a] text-white p-6">
          <Lock className="h-16 w-16 text-primary" />
          <div className="text-center max-w-sm">
            <h1 className="text-2xl font-bold mb-2">Pannello Admin protetto</h1>
            <p className="text-slate-400 mb-6">Inserisci la password di amministrazione per accedere.</p>
            <div className="flex flex-col gap-3">
              <input
                type="password"
                value={adminPasswordInput}
                onChange={(e) => setAdminPasswordInput(e.target.value)}
                onKeyDown={handleAdminPasswordKeyDown}
                placeholder="Password admin..."
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              {adminPasswordError && (
                <p className="text-sm text-red-400">{adminPasswordError}</p>
              )}
              <button
                onClick={verifyAdminPassword}
                className="w-full rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Accedi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog password catalogo pubblico */}
      {catalogPasswordDialogOpen && !isAdmin && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-[#0f172a] text-white p-6">
          <Lock className="h-16 w-16 text-primary" />
          <div className="text-center max-w-sm">
            <h1 className="text-2xl font-bold mb-2">Catalogo protetto</h1>
            <p className="text-slate-400 mb-6">Inserisci la password per accedere al catalogo film.</p>
            <div className="flex flex-col gap-3">
              <input
                type="password"
                value={catalogPasswordInput}
                onChange={(e) => setCatalogPasswordInput(e.target.value)}
                onKeyDown={handleCatalogPasswordKeyDown}
                placeholder="Password..."
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary"
                autoFocus
              />
              {catalogPasswordError && (
                <p className="text-sm text-red-400">{catalogPasswordError}</p>
              )}
              <button
                onClick={verifyCatalogPassword}
                className="w-full rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Accedi
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLocalhost && isLandscapeMobile && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-4 bg-black/95 text-white p-6">
          <RotateCw className="h-16 w-16 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-xl font-bold">Ruota il telefono</p>
            <p className="mt-2 text-sm text-gray-300">
              Per una migliore esperienza, utilizza il catalogo in verticale.
            </p>
          </div>
        </div>
      )}
      <Toaster position="top-right" richColors />

      {/* Wrapper sticky: header + barra strumenti */}
      <div className="sticky top-0 z-50 border-b shadow-md bg-background">
        {/* Riga 1: titolo + ricerche */}
        <div className="bg-card px-4 md:px-6 lg:px-8 xl:px-12 py-2">
          <div className="mx-auto flex w-full flex-col gap-2">
            {/* Prima riga: titolo/manutenzione + ricerche */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-wrap items-center gap-2 py-1">
                <Clapperboard className="h-6 w-6 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">
                  {!isAdmin ? "Catalogo film Emby" : "Gestione Film"}
                </h1>
                {isAdmin && (
                  <Button
                    variant={maintenanceMode ? "destructive" : "outline"}
                    size="sm"
                    onClick={toggleMaintenance}
                  >
                    <AlertTriangle className="mr-2 h-4 w-4" />
                    {maintenanceMode ? "Disattiva manutenzione" : "Attiva manutenzione"}
                  </Button>
                )}
                {!isAdmin && <Badge variant="secondary" className="hidden md:inline-flex">Sola lettura</Badge>}
              </div>

              <div className="flex flex-1 flex-col gap-2 md:flex-row md:items-center md:ml-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={isAdmin ? "Cerca nei tuoi film..." : "digita qui il titolo che vuoi cercare"}
                  className="pl-9 pr-10 h-9"
                  value={localQuery}
                  onChange={(e) => setLocalQuery(e.target.value)}
                />
                {localQuery && (
                  <button
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded bg-red-500 text-white hover:bg-red-600 shadow-sm"
                    onClick={() => setLocalQuery("")}
                    title="Cancella ricerca"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {isAdmin && (
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Cerca su TheMovieDB..."
                    className="pl-9 h-9"
                    value={tmdbQuery}
                    onChange={(e) => setTmdbQuery(e.target.value)}
                  />
                  {tmdbQuery && (
                    <button
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded bg-red-500 text-white hover:bg-red-600 shadow-sm"
                      onClick={() => {
                        setTmdbQuery("");
                        setTmdbResults([]);
                      }}
                      title="Cancella ricerca TMDB"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
            </div>

            {/* Seconda riga: pulsanti admin */}
            {isAdmin && (
              <div className="flex flex-wrap items-center gap-2 py-1">
                <Badge
                  variant="outline"
                  className="text-[10px] shrink-0"
                  title="Versione del catalogo: cambia a ogni aggiornamento"
                >
                  {APP_VERSION}
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openSyncDialog}
                  disabled={syncing}
                  title="Sincronizza il catalogo con Emby"
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                  Sincronizza
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetAllNewEpisodes}
                  disabled={resettingNewEpisodes}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                  title="Resetta tutti i badge 'Nuovi Episodi' per tutte le serie TV"
                >
                  <BellOff className={`mr-2 h-4 w-4 ${resettingNewEpisodes ? "animate-spin" : ""}`} /> Reset nuovi episodi
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openEmby}
                >
                  <Download className="mr-2 h-4 w-4" /> Importa da Emby
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runPopulateActors}
                  disabled={populatingActors}
                >
                  <Users className={`mr-2 h-4 w-4 ${populatingActors ? "animate-pulse" : ""}`} /> Popola attori
                </Button>
                <label className="flex items-center gap-1 text-[11px] text-muted-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={forcePopulateActors}
                    onChange={(e) => setForcePopulateActors(e.target.checked)}
                    className="accent-primary h-3 w-3"
                  />
                  Forza
                </label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[11px] px-2 h-7"
                  onClick={runResetEmptyActors}
                  disabled={resettingEmptyActors}
                  title="Resetta i film con attori vuoti per poterli ri-processare"
                >
                  <RotateCcw className={`mr-1 h-3 w-3 ${resettingEmptyActors ? "animate-spin" : ""}`} /> Reset vuoti
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runPopulateResolution}
                  disabled={populatingResolution}
                  title="Popola la risoluzione (720/1080/4K) dai metadati Emby"
                >
                  <Activity className={`mr-2 h-4 w-4 ${populatingResolution ? "animate-spin" : ""}`} /> Popola risoluzioni
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runSyncTmdbIds}
                  disabled={syncingTmdbIds}
                >
                  <Link className={`mr-2 h-4 w-4 ${syncingTmdbIds ? "animate-pulse" : ""}`} /> Sincronizza TMDB IDs
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDuplicateOpen(true);
                    setDuplicateSearched(false);
                    setDuplicateData(null);
                  }}
                  disabled={duplicateLoading}
                >
                  <Copy className={`mr-2 h-4 w-4 ${duplicateLoading ? "animate-spin" : ""}`} /> Cerca duplicati
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNonStandardOpen(true);
                    setNonStandardSearched(false);
                    setNonStandardData(null);
                  }}
                  disabled={nonStandardLoading}
                >
                  <AlertTriangle className={`mr-2 h-4 w-4 ${nonStandardLoading ? "animate-spin" : ""}`} /> File non standard
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setNoCoverOpen(true);
                    setNoCoverSearched(false);
                    setNoCoverData(null);
                  }}
                  disabled={noCoverLoading}
                >
                  <ImageOff className={`mr-2 h-4 w-4 ${noCoverLoading ? "animate-spin" : ""}`} /> Senza copertina
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAdminRequestUserFilter("");
                    setAdminRequestStatusFilter("");
                    setAdminRequestsOpen(true);
                    fetchAdminRequests();
                    fetchRequestViews();
                  }}
                  disabled={adminRequestsLoading}
                >
                  <MessageSquare className={`mr-2 h-4 w-4 ${adminRequestsLoading ? "animate-spin" : ""}`} /> Richieste
                  {adminPendingCount > 0 && (
                    <span className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
                      {adminPendingCount}
                    </span>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setActiveUsersOpen(true);
                    fetchActiveUsers();
                  }}
                  disabled={activeUsersLoading}
                >
                  <Activity className={`mr-2 h-4 w-4 ${activeUsersLoading ? "animate-spin" : ""}`} /> Utenti attivi
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openHistoryDialog}
                  disabled={historyLoading}
                >
                  <History className={`mr-2 h-4 w-4 ${historyLoading ? "animate-spin" : ""}`} /> Storico visioni
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setAdminAnnouncementOpen(true);
                    fetchAdminAnnouncements();
                  }}
                >
                  <Megaphone className="mr-2 h-4 w-4" /> Annunci
                </Button>

                {actorsStatus && (
                  <span className="text-[10px] text-muted-foreground ml-1">
                    {actorsStatus.with_actors}/{actorsStatus.total} ✅
                  </span>
                )}
                {resolutionStatus && (
                  <span className="text-[10px] text-muted-foreground ml-1">
                    {resolutionStatus.with_resolution}/{resolutionStatus.total} 🎬
                  </span>
                )}
                {syncTmdbStatus && syncTmdbStatus.missing > 0 && (
                  <button
                    className="text-[10px] text-orange-500 ml-1 hover:underline cursor-pointer"
                    onClick={async () => {
                      try {
                        const { data } = await api.get("/api/admin/missing-tmdb-ids");
                        setMissingTmdbList(data.films);
                        setMissingTmdbOpen(true);
                      } catch (err) {
                        toast.error("Errore caricamento lista");
                      }
                    }}
                  >
                    {syncTmdbStatus.missing} senza TMDB ID
                  </button>
                )}
                <button
                  className="text-[10px] text-blue-500 ml-1 hover:underline cursor-pointer inline-flex items-center gap-1"
                  onClick={() => {
                    setFilesSizeOpen(true);
                    if (!filesSizeData) fetchFilesBySize();
                  }}
                >
                  <HardDrive className="h-3 w-3" /> File per dimensione
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Riga 2: barra strumenti */}
        <div className="px-4 md:px-6 lg:px-8 xl:px-12 py-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button size="sm" variant="outline" className="md:hidden" onClick={openSyncDialog} disabled={syncing}>
                  <RefreshCw className={`mr-1 h-3 w-3 ${syncing ? "animate-spin" : ""}`} /> Sync
                </Button>
              )}
            </div>

            {!isAdmin && (
              <div className="w-full flex justify-center gap-2 flex-wrap">
                <Button size="sm" className="bg-green-600 text-white hover:bg-green-700 relative" onClick={() => setRequestOpen(true)}>
                  <Send className="mr-1 h-3 w-3" /> Richiesta film
                  {unseenResponses > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white shadow">
                      {unseenResponses}
                    </span>
                  )}
                </Button>
                <Button size="sm" variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50" onClick={() => setReportOpen(true)}>
                  <AlertTriangle className="mr-1 h-3 w-3" /> Segnalazioni
                </Button>
                {notifPermission === "default" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-blue-400 text-blue-600 hover:bg-blue-50"
                    onClick={requestNotificationPermission}
                    title="Attiva le notifiche per ricevere un avviso quando rispondiamo alle tue richieste"
                  >
                    <BellOff className="mr-1 h-3 w-3" /> Attiva notifiche
                  </Button>
                )}
                {notifPermission === "granted" && (
                  <span className="inline-flex items-center gap-1 text-[10px] text-green-600" title="Notifiche attive">
                    <Bell className="h-3 w-3" /> Notifiche on
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode("grid")}
                className={`rounded p-1.5 transition-colors ${viewMode === "grid" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                title="Vista griglia"
              >
                <Grid2x2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`rounded p-1.5 transition-colors ${viewMode === "list" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                title="Vista lista"
              >
                <List className="h-4 w-4" />
              </button>
              <span className="mx-1 h-4 w-px bg-border" />
              <button
                onClick={() => {
                  setSortBy("added");
                  toast.info("Ordinamento per ultimo inserimento attivato");
                }}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${sortBy === "added" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                title="Ultimo inserimento"
              >
                Data ↓
              </button>
              <button
                onClick={() => setSortBy("az")}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${sortBy === "az" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                title="Ordine 0-Z"
              >
                0-Z
              </button>
              <button
                onClick={() => setSortBy("za")}
                className={`rounded px-2 py-1 text-xs font-medium transition-colors ${sortBy === "za" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                title="Ordine Z-0"
              >
                Z-0
              </button>
              {viewMode === "grid" && (
                <>
                  <span className="mx-1 h-4 w-px bg-border" />
                  <button
                    onClick={() => setGridCols(3)}
                    className={`rounded p-1.5 transition-colors ${gridCols <= 3 ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                    title="Griglia larga"
                  >
                    <LayoutGrid className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setGridCols(5)}
                    className={`rounded p-1.5 transition-colors ${gridCols > 3 && gridCols < 7 ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                    title="Griglia media"
                  >
                    <Grid3x3 className="h-4 w-4" />
                  </button>
                </>
              )}
              <span className="mx-1 h-4 w-px bg-border" />
              <button
                onClick={() => setIsDark(!isDark)}
                className="rounded p-1.5 transition-colors bg-muted hover:bg-accent text-muted-foreground"
                title={isDark ? "Modalità chiara" : "Modalità scura"}
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <a
                href="/manuale-utenti.html"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded p-1.5 transition-colors bg-muted hover:bg-accent text-muted-foreground inline-flex items-center"
                title="Manuale d'uso"
              >
                <HelpCircle className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Riga 3: Filtri catalogo */}
        <div className="px-3 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-2 bg-background border-t">
          <div className="flex items-end gap-3">
            <div className="flex-1 min-w-0">
              <label className="text-[10px] uppercase font-semibold text-muted-foreground mb-1 block tracking-wide">Categoria</label>
              <select
                className="w-full rounded border bg-card px-2 py-1.5 text-sm font-medium"
                value={activeCategoria || ""}
                onChange={(e) => {
                  setActiveCategoria(e.target.value || null);
                  setActiveAnno(null);
                  setVisiblePages(1);
                }}
              >
                <option value="">📁 Tutto il catalogo</option>
                {CATEGORIE.map((cat) => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            {availableYears.length > 0 && (
              <div className="flex-1 min-w-0">
                <label className="text-[10px] uppercase font-semibold text-muted-foreground mb-1 block tracking-wide">Anno</label>
                <select
                  className="w-full rounded border bg-card px-2 py-1.5 text-sm font-medium"
                  value={activeAnno || ""}
                  onChange={(e) => {
                    setActiveAnno(e.target.value ? Number(e.target.value) : null);
                    setVisiblePages(1);
                  }}
                >
                  <option value="">Tutti gli anni</option>
                  {availableYears.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="shrink-0 pb-1.5 text-right">
              <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">Film</div>
              <div className="text-lg font-bold text-primary leading-none">{displayFilms.length}</div>
            </div>
          </div>

          {debouncedLocal.trim() && (
            <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-red-50 border border-red-200 px-3 py-1.5">
              <div className="flex items-center gap-2 text-xs text-red-700">
                <Search className="h-3.5 w-3.5 shrink-0" />
                <span className="leading-tight">
                  <strong>Ricerca: "{debouncedLocal}"</strong>
                  <span className="block text-[10px] opacity-90">Chiudi la ricerca cliccando sulla X se desideri passare alla ricerca per categoria e anno.</span>
                </span>
              </div>
              <button
                onClick={() => setLocalQuery("")}
                className="flex h-6 w-6 items-center justify-center rounded bg-red-500 text-white hover:bg-red-600 shadow-sm"
                title="Cancella ricerca"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {tmdbResults.length > 0 && isAdmin && (
        <div className="mx-auto w-full px-4 md:px-6 lg:px-8 xl:px-12 pt-4">
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-muted-foreground">
                Risultati TheMovieDB
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTmdbQuery("");
                  setTmdbResults([]);
                }}
              >
                <X className="mr-1 h-3 w-3" /> Chiudi
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tmdbResults.map((m) => (
                <div
                  key={m.id}
                  className="flex gap-3 rounded-md border p-3 transition-colors hover:bg-accent"
                >
                  {m.poster_url ? (
                    <img
                      src={m.poster_url}
                      alt={m.title}
                      className="h-24 w-16 rounded object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-24 w-16 items-center justify-center rounded bg-muted">
                      <Film className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex flex-1 flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium leading-tight">{m.title}</p>
                        {m.media_type === "tv" && (
                          <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">SERIE TV</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {m.release_date?.split("-")[0] || "N/A"}
                      </p>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                        {m.overview}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="mt-2 w-fit"
                      onClick={() => addFromTmdb(m.id, m.media_type)}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Aggiungi
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {tmdbLoading && (
        <div className="mx-auto w-full px-4 md:px-6 lg:px-8 xl:px-12 pt-2 text-sm text-muted-foreground">
          Ricerca su TMDB in corso...
        </div>
      )}

      <main className="mx-auto flex w-full gap-6 px-4 md:px-6 lg:px-8 xl:px-12 py-6">
        <aside className="hidden h-fit w-52 flex-shrink-0 md:block sticky top-40 self-start overflow-y-auto max-h-[calc(100vh-10rem)]">
          <div className="space-y-4">
            {isAdmin && (
              <Button className="w-full" onClick={openAdd}>
                <Plus className="mr-2 h-4 w-4" /> Aggiungi Film
              </Button>
            )}

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Filtra A-Z
              </h3>
              <div className="flex flex-wrap gap-1">
                {letters.map((l) => (
                  <button
                    key={l}
                    onClick={() => setActiveAZ(activeAZ === l ? null : l)}
                    className={`h-7 w-7 rounded text-xs font-medium transition-colors ${
                      activeAZ === l
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted hover:bg-accent"
                    }`}
                  >
                    {l}
                  </button>
                ))}
                <button
                  onClick={() =>
                    setActiveAZ(activeAZ === "0-9" ? null : "0-9")
                  }
                  className={`h-7 rounded px-2 text-xs font-medium transition-colors ${
                    activeAZ === "0-9"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted hover:bg-accent"
                  }`}
                >
                  0-9
                </button>
                <button
                  onClick={() => setActiveResolution(activeResolution === "720" ? null : "720")}
                  className={`h-7 rounded px-2 text-xs font-medium text-white transition-all ${
                    activeResolution === "720" ? "opacity-100 ring-2 ring-white" : "opacity-80 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: "#3b82f6" }}
                  title="Filtra 720"
                >
                  720
                </button>
                <button
                  onClick={() => setActiveResolution(activeResolution === "1080" ? null : "1080")}
                  className={`h-7 rounded px-2 text-xs font-medium text-white transition-all ${
                    activeResolution === "1080" ? "opacity-100 ring-2 ring-white" : "opacity-80 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: "#22c55e" }}
                  title="Filtra 1080"
                >
                  1080
                </button>
                <button
                  onClick={() => setActiveResolution(activeResolution === "4K" ? null : "4K")}
                  className={`h-7 rounded px-2 text-xs font-medium text-white transition-all ${
                    activeResolution === "4K" ? "opacity-100 ring-2 ring-white" : "opacity-80 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: "#eab308" }}
                  title="Filtra 4K"
                >
                  4K
                </button>
                <button
                  onClick={() => setActiveResolution(activeResolution === "SD" ? null : "SD")}
                  className={`h-7 rounded px-2 text-xs font-medium text-white transition-all ${
                    activeResolution === "SD" ? "opacity-100 ring-2 ring-white" : "opacity-80 hover:opacity-100"
                  }`}
                  style={{ backgroundColor: "#9ca3af" }}
                  title="Filtra SD"
                >
                  SD
                </button>
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                Categorie
              </h3>
              <button
                onClick={() => {
                  setActiveAZ(null);
                  setActiveDrive(null);
                  setActiveCategoria(null);
                  setActiveAnno(null);
                  setActiveResolution(null);
                  setLocalQuery("");
                }}
                className={`mb-4 flex w-full items-center justify-between rounded px-2 py-1 text-xs font-medium transition-colors ${activeDrive || activeCategoria || activeAnno || activeResolution ? "bg-red-500 text-white hover:bg-red-600 animate-pulse" : "bg-green-500 text-black hover:bg-green-600"}`}
              >
                <span className="flex items-center gap-1">
                  {activeDrive || activeCategoria || activeResolution ? <RotateCcw className="h-3 w-3" /> : null}
                  {activeDrive || activeCategoria || activeResolution ? "RESETTA FILTRI" : "TUTTI I FILM"}
                </span>
                <span>{categoryCounts.total}</span>
              </button>



              <div className="flex flex-col gap-1">
                {CATEGORIE.map((cat) => {
                  const catBg = (isAdmin && hiddenCategories.includes(cat)) ? "#9ca3af" : (CATEGORY_COLORS[cat] || "#e5e7eb");
                  const isActive = activeCategoria === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCategoria(isActive ? null : cat)}
                      className={`flex w-full items-center justify-between rounded px-2 py-1 text-xs font-medium text-black transition-colors ${isActive ? "font-bold" : ""}`}
                      style={{
                        backgroundColor: catBg,
                        boxShadow: isActive ? `inset 0 0 0 2px #000` : "none",
                      }}
                    >
                      <span className="flex-1 min-w-0 truncate text-left">{cat}</span>
                      {isAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (hiddenCategories.includes(cat)) {
                              // Mostra direttamente senza conferma
                              const next = hiddenCategories.filter((c) => c !== cat);
                              saveHiddenCategories(next);
                            } else {
                              // Chiedi conferma prima di nascondere
                              setConfirmHideCategory(cat);
                            }
                          }}
                          className="ml-2 mr-1 text-black/50 hover:text-black shrink-0"
                          title={hiddenCategories.includes(cat) ? "Categoria nascosta — clicca per mostrare" : "Categoria visibile — clicca per nascondere"}
                        >
                          {hiddenCategories.includes(cat) ? (
                            <EyeOff className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                        </button>
                      )}
                      <span className="shrink-0">{categoryCounts[cat] || 0}</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedCategorySummary(cat);
                          setCategorySummaryOpen(true);
                        }}
                        className="ml-1 text-black/40 hover:text-black shrink-0"
                        title="Riepilogo drive per questa categoria"
                      >
                        <FolderOpen className="h-3 w-3" />
                      </button>
                    </button>
                  );
                })}
              </div>
            </div>

            {isAdmin && drives.length > 0 && (
              <div>
                <h3 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground">
                  <HardDrive className="h-3 w-3" /> Drive
                </h3>
                <div className="flex flex-wrap gap-1">
                  {drives.map((d) => (
                    <button
                      key={d}
                      onClick={() => {
                        setSelectedDriveSummary(d);
                        setDriveSummaryOpen(true);
                      }}
                      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                        activeDrive === d
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted hover:bg-accent"
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2 flex items-center gap-1 text-xs font-semibold uppercase text-muted-foreground">
                <ArrowUpDown className="h-3 w-3" /> Ordina
              </h3>
              <select
                className="w-full rounded border bg-background px-2 py-1.5 text-xs"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="added">Ultimo inserimento</option>
                <option value="az">Alfabetico (A-Z)</option>
                <option value="za">Alfabetico (Z-A)</option>
                <option value="year">Anno (recente prima)</option>
              </select>
            </div>

            {(activeAZ || activeDrive || activeAnno || activeResolution || localQuery) && (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => {
                  setActiveAZ(null);
                  setActiveDrive(null);
                  setActiveCategoria(null);
                  setActiveAnno(null);
                  setActiveResolution(null);
                  setLocalQuery("");
                }}
              >
                <X className="mr-1 h-3 w-3" /> Resetta filtri
              </Button>
            )}


          </div>
        </aside>

        <section className="flex-1 min-w-0">
          {loading ? (
            <div className="py-20 text-center text-muted-foreground">
              <FilmStripLoader className="mx-auto mb-4 h-10 w-10 text-primary" />
              <p className="text-lg font-medium">Sto caricando il catalogo...</p>
              <p className="mt-2 text-sm">La prima volta può richiedere qualche secondo, poi sarà istantaneo.</p>
            </div>
          ) : displayFilms.length === 0 ? (
            <div className="py-20 text-center text-muted-foreground">
              {loadError ? (
                <>
                  <AlertCircle className="mx-auto mb-3 h-10 w-10 text-red-500" />
                  <p className="font-medium text-red-600">{loadError}</p>
                  <Button variant="outline" className="mt-4" onClick={fetchFilms}>
                    <RotateCcw className="mr-2 h-4 w-4" /> Riprova
                  </Button>
                </>
              ) : (activeDrive && !activeCategoria) ? (
                <>
                  <AlertCircle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
                  <p className="font-medium text-amber-600">Hai selezionato il drive <strong>{activeDrive}</strong></p>
                  <p className="mt-1 text-sm">Seleziona una categoria dal menu a sinistra per vedere i film.</p>
                </>
              ) : (!activeDrive && activeCategoria && isAdmin) ? (
                <>
                  <AlertCircle className="mx-auto mb-3 h-10 w-10 text-amber-500" />
                  <p className="font-medium text-amber-600">Hai selezionato la categoria <strong>{activeCategoria}</strong></p>
                  <p className="mt-1 text-sm">Seleziona un drive dal menu a sinistra per vedere i film.</p>
                </>
              ) : (
                <>
                  <Film className="mx-auto mb-3 h-10 w-10 opacity-50" />
                  <p>Nessun film trovato.</p>
                </>
              )}
              {!loadError && isAdmin && (
                <Button variant="outline" className="mt-4" onClick={openAdd}>
                  <Plus className="mr-2 h-4 w-4" /> Aggiungi il primo film
                </Button>
              )}
            </div>
          ) : (
            <>
              {viewMode === "list" ? (
              <div className="flex flex-col gap-2">
                {displayFilms.slice(0, visibleCount).map((film) => (
                  <FilmListItem
                    key={film.id}
                    film={film}
                    latestDate={latestDate}
                    onClick={setDetailFilm}
                  />
                ))}
              </div>
            ) : (
              <div className={`grid ${isMobile ? "gap-2" : "gap-4"}`} style={{ gridTemplateColumns: `repeat(${effectiveGridCols}, minmax(0, 1fr))` }}>
                {displayFilms.slice(0, visibleCount).map((film) => (
                  <FilmCard
                    key={film.id}
                    film={film}
                    latestDate={latestDate}
                    readonly={!isAdmin}
                    onEdit={openEdit}
                    onDelete={setDeleteId}
                    onClick={setDetailFilm}
                    compact={isMobile || gridCols >= 5}
                    isHiddenCategory={hiddenCategories.includes(film.categoria)}
                  />
                ))}
              </div>
            )}
              {displayFilms.length > visibleCount && (
                <div className="mt-8 flex justify-center">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setVisiblePages((p) => p + 1)}
                  >
                    Carica altri ({displayFilms.length - visibleCount} rimasti)
                  </Button>
                </div>
              )}
            </>
          )}


        </section>
      </main>

      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="h-[85vh] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                {editingFilm ? "Modifica Film" : "Aggiungi Film"}
              </DialogTitle>
              <DialogDescription>
                {editingFilm
                  ? "Modifica i dati del film selezionato."
                  : "Inserisci i dati del film manualmente."}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="titolo">Titolo *</Label>
                <Input
                  id="titolo"
                  value={form.titolo}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, titolo: e.target.value }))
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="anno">Anno</Label>
                  <Input
                    id="anno"
                    type="number"
                    value={form.anno}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, anno: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="voto">Voto (0-10)</Label>
                  <Input
                    id="voto"
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    value={form.voto_personale}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        voto_personale: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="genere">Genere</Label>
                <Input
                  id="genere"
                  value={form.genere}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, genere: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="regista">Regista</Label>
                <Input
                  id="regista"
                  value={form.regista}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, regista: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="attori">Attori</Label>
                <Input
                  id="attori"
                  value={form.attori}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, attori: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="copertina">URL Copertina</Label>
                <Input
                  id="copertina"
                  value={form.copertina_url}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      copertina_url: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="percorso">Percorso File</Label>
                <Input
                  id="percorso"
                  value={form.percorso_file}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      percorso_file: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="emby_id">Emby ID</Label>
                <Input
                  id="emby_id"
                  value={form.emby_id}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      emby_id: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Categoria *</Label>
                <div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
                  {CATEGORIE.map((cat) => (
                    <label key={cat} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="categoria"
                        value={cat}
                        checked={form.categoria === cat}
                        onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
                      />
                      {cat}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea
                  id="descrizione"
                  rows={3}
                  value={form.descrizione}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      descrizione: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              {!editingFilm && (
                <Button variant="outline" onClick={clearForm}>
                  Cancella Campi
                </Button>
              )}
              <Button onClick={saveFilm}>
                {editingFilm ? "Salva Modifiche" : "Aggiungi"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {deleteId && (
        <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conferma eliminazione</DialogTitle>
              <DialogDescription>
                Sei sicuro di voler eliminare questo film? L'azione è
                irreversibile.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteId(null)}>
                Annulla
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                <Trash2 className="mr-2 h-4 w-4" /> Elimina
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {tmdbPreview && (
        <Dialog open={!!tmdbPreview} onOpenChange={() => setTmdbPreview(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>Aggiungi da TheMovieDB</DialogTitle>
              <DialogDescription>
                Verifica i dati e inserisci il percorso file prima di salvare.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-6 md:grid-cols-[200px_1fr]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-muted">
                {tmdbPreview.copertina_url ? (
                  <img
                    src={resolveImageUrl(tmdbPreview.copertina_url)}
                    alt={tmdbPreview.titolo}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                    <Film className="h-12 w-12" />
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-4 min-w-0">
                <div>
                  <h3 className="text-lg font-bold">{tmdbPreview.titolo}</h3>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                    {tmdbPreview.anno && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" /> {tmdbPreview.anno}
                      </span>
                    )}
                    {tmdbPreview.regista && (
                      <span className="flex items-center gap-1">
                        <User className="h-4 w-4" /> {tmdbPreview.regista}
                      </span>
                    )}
                  </div>
                </div>
                {tmdbPreview.genere && (
                  <div className="flex flex-wrap gap-2">
                    {tmdbPreview.genere.split(",").map((g) => (
                      <Badge key={g} variant="secondary">
                        {g.trim()}
                      </Badge>
                    ))}
                  </div>
                )}
                {tmdbPreview.descrizione && (
                  <p className="text-sm leading-relaxed">{tmdbPreview.descrizione}</p>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="tmdb-percorso">Percorso File</Label>
                  <Input
                    id="tmdb-percorso"
                    placeholder="D:\Film\..."
                    value={tmdbForm.percorso_file}
                    onChange={(e) =>
                      setTmdbForm((f) => ({ ...f, percorso_file: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="tmdb-voto">Voto personale (0-10)</Label>
                  <Input
                    id="tmdb-voto"
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    placeholder="0-10"
                    value={tmdbForm.voto_personale}
                    onChange={(e) =>
                      setTmdbForm((f) => ({ ...f, voto_personale: e.target.value }))
                    }
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Categoria *</Label>
                  <div className="flex flex-col gap-1 max-h-32 overflow-y-auto">
                    {CATEGORIE.map((cat) => (
                      <label key={cat} className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="tmdb-categoria"
                          value={cat}
                          checked={tmdbForm.categoria === cat}
                          onChange={(e) => setTmdbForm((f) => ({ ...f, categoria: e.target.value }))}
                        />
                        {cat}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTmdbPreview(null)}>
                Annulla
              </Button>
              <Button onClick={saveTmdbPreview}>Salva nel database</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {detailFilm && (
        <Dialog open={!!detailFilm} onOpenChange={() => {
          setDetailFilm(null);
          setDetailImgError(false);
          setDebugFilmData(null);
          setDebugEmbyData(null);
          setDebugResolutionData(null);
          if (returnToMissingTmdb) {
            setReturnToMissingTmdb(false);
            setMissingTmdbOpen(true);
          }
        }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden max-w-7xl w-[98vw]">
            <div className="grid gap-6 md:grid-cols-[280px_1fr]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg bg-muted">
                {detailFilm.copertina_url && !detailImgError ? (
                  <img
                    src={resolveImageUrl(detailFilm.copertina_url)}
                    alt={detailFilm.titolo}
                    className="h-full w-full object-cover"
                    onError={() => setDetailImgError(true)}
                  />
                ) : (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
                    <Film className="h-16 w-16" />
                    <span className="text-xs">Nessuna copertina</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-4">
                <DialogHeader>
                  <DialogTitle className="text-2xl">{detailFilm.titolo}</DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {detailFilm.anno && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" /> {detailFilm.anno}
                    </span>
                  )}
                  {detailFilm.regista && (
                    <span className="flex items-center gap-1">
                      <User className="h-4 w-4" /> {detailFilm.regista}
                    </span>
                  )}
                  {detailFilm.attori && (
                    <span className="flex items-center gap-1 flex-wrap">
                      <User className="h-4 w-4 shrink-0" />
                      {detailFilm.attori.split(',').map((actor, idx) => {
                        const trimmed = actor.trim();
                        return (
                          <React.Fragment key={idx}>
                            {idx > 0 && <span className="text-muted-foreground">,</span>}
                            <button
                              className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0 text-sm"
                              onClick={() => searchByActor(trimmed)}
                            >
                              {trimmed}
                            </button>
                          </React.Fragment>
                        );
                      })}
                    </span>
                  )}
                  {detailFilm.voto_personale != null && (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <Star className="h-4 w-4 fill-yellow-500" /> {detailFilm.voto_personale}
                    </span>
                  )}
                  {detailFilm.risoluzione && getResolutionStyle(detailFilm.risoluzione) && (
                    <span
                      className="inline-flex items-center rounded px-2 py-0.5 text-[11px] font-bold text-white"
                      style={{ backgroundColor: getResolutionStyle(detailFilm.risoluzione).bg }}
                    >
                      {getResolutionStyle(detailFilm.risoluzione).label}
                    </span>
                  )}
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <select
                        className="h-6 rounded border border-input bg-background px-1 text-xs text-foreground"
                        value={detailFilm.risoluzione || ""}
                        onChange={async (e) => {
                          const val = e.target.value || null;
                          try {
                            await api.put(`/api/films/${detailFilm.id}`, { risoluzione: val });
                            const updated = { ...detailFilm, risoluzione: val };
                            setDetailFilm(updated);
                            setAllFilms((prev) => prev.map((f) => (f.id === detailFilm.id ? { ...f, risoluzione: val } : f)));
                            setDisplayFilms((prev) => prev.map((f) => (f.id === detailFilm.id ? { ...f, risoluzione: val } : f)));
                            toast.success("Risoluzione aggiornata");
                          } catch (err) {
                            toast.error("Errore aggiornamento risoluzione");
                          }
                        }}
                      >
                        <option value="">—</option>
                        <option value="720">720</option>
                        <option value="1080">1080</option>
                        <option value="4K">4K</option>
                        <option value="SD">SD</option>
                      </select>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-6 px-2 text-[10px]"
                        disabled={refreshingResolution || !detailFilm.emby_id || detailFilm.tipo === "Series"}
                        title={
                          detailFilm.tipo === "Series"
                            ? "Le serie TV non hanno una risoluzione unica su Emby"
                            : detailFilm.emby_id
                              ? "Ricalcola risoluzione da Emby"
                              : "Nessun ID Emby associato"
                        }
                        onClick={async () => {
                          if (!detailFilm.emby_id) return;
                          setRefreshingResolution(true);
                          try {
                            const { data } = await api.post(`/api/admin/films/${detailFilm.id}/refresh-resolution`);
                            if (data.updated) {
                              toast.success(`Risoluzione aggiornata: ${data.risoluzione}`);
                            } else {
                              toast.info(`Risoluzione già corretta: ${data.risoluzione}`);
                            }
                            const updated = { ...detailFilm, risoluzione: data.risoluzione };
                            setDetailFilm(updated);
                            setAllFilms((prev) => prev.map((f) => (f.id === detailFilm.id ? { ...f, risoluzione: data.risoluzione } : f)));
                            setDisplayFilms((prev) => prev.map((f) => (f.id === detailFilm.id ? { ...f, risoluzione: data.risoluzione } : f)));
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Errore ricalcolo risoluzione da Emby");
                          } finally {
                            setRefreshingResolution(false);
                          }
                        }}
                      >
                        <RefreshCw className={`mr-1 h-3 w-3 ${refreshingResolution ? "animate-spin" : ""}`} />
                        Emby
                      </Button>
                    </div>
                  )}
                </div>
                {detailFilm.genere && (
                  <div className="flex flex-wrap gap-2">
                    {detailFilm.genere.split(",").map((g) => (
                      <Badge key={g} variant="secondary">
                        {g.trim()}
                      </Badge>
                    ))}
                  </div>
                )}
                {detailFilm.categoria && (
                  <Badge variant="outline">{detailFilm.categoria}</Badge>
                )}
                {detailFilm.descrizione && (
                  <p className="text-sm leading-relaxed text-foreground">
                    {detailFilm.descrizione}
                  </p>
                )}
                {isAdmin && (
                  <div className="flex flex-wrap items-center gap-2">
                    {detailFilm.has_new_episodes && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-orange-500 text-orange-600 hover:bg-orange-50"
                        onClick={() => markEpisodesSeen(detailFilm.id)}
                      >
                        Segna episodi come visti
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/api/admin/film-debug/${detailFilm.id}`);
                          setDebugFilmData(data);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || err.message || "Errore nel debug");
                        }
                      }}
                    >
                      Debug TMDB
                    </Button>
                    {detailFilm.emby_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/api/admin/emby-debug/${detailFilm.emby_id}`);
                            setDebugEmbyData(data);
                          } catch (err) {
                            if (err.response?.status === 404) {
                              toast.warning(err.response?.data?.detail || "Emby ID non trovato");
                            } else {
                              toast.error(err.response?.data?.detail || err.message || "Errore debug Emby");
                            }
                          }
                        }}
                      >
                        Debug Emby
                      </Button>
                    )}
                    {detailFilm.tmdb_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        onClick={async () => {
                          try {
                            const { data } = await api.post(`/api/admin/fix-cast-single/${detailFilm.id}`);
                            if (data.success) {
                              toast.success(`Cast aggiornato: ${data.attori}`);
                              setDetailFilm({ ...detailFilm, attori: data.attori, tipo: data.tipo });
                            } else {
                              toast.warning(data.error || "Nessun cast trovato su TMDB");
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Errore correzione cast");
                          }
                        }}
                      >
                        Correggi cast TMDB
                      </Button>
                    )}
                  </div>
                )}
                {debugFilmData && (
                  <div className="rounded border bg-muted p-3 text-xs font-mono space-y-1">
                    <p><strong>DB:</strong> {debugFilmData.db_titolo} ({debugFilmData.db_anno})</p>
                    <p><strong>tmdb_id:</strong> {debugFilmData.db_tmdb_id}</p>
                    <p><strong>Attori DB:</strong> {debugFilmData.db_attori || "—"}</p>
                    {debugFilmData.tmdb_movie && (
                      <p className="text-blue-600">🎬 TMDB Movie: {debugFilmData.tmdb_movie.title} ({debugFilmData.tmdb_movie.release_date}) → Cast: {debugFilmData.tmdb_movie.cast_preview?.join(", ") || "—"}</p>
                    )}
                    {debugFilmData.tmdb_tv && (
                      <p className="text-green-600">📺 TMDB TV: {debugFilmData.tmdb_tv.name} ({debugFilmData.tmdb_tv.first_air_date}) → Cast: {debugFilmData.tmdb_tv.cast_preview?.join(", ") || "—"}</p>
                    )}
                    {!debugFilmData.tmdb_movie && !debugFilmData.tmdb_tv && (
                      <p className="text-red-600">❌ Nessun risultato TMDB per questo tmdb_id</p>
                    )}
                  </div>
                )}
                {debugEmbyData && (
                  <div className="rounded border bg-muted p-3 text-xs font-mono space-y-1">
                    <p><strong>Emby ID:</strong> {debugEmbyData.emby_id}</p>
                    <p><strong>Titolo Emby:</strong> {debugEmbyData.titolo}</p>
                    <p><strong>Tipo:</strong> {debugEmbyData.tipo}</p>
                    <p><strong>ProviderIds raw:</strong> {JSON.stringify(debugEmbyData.provider_ids_raw)}</p>
                    <p><strong>tmdb_id estratto:</strong> {debugEmbyData.tmdb_id_extracted || "—"}</p>
                    <p><strong>Path:</strong> {debugEmbyData.path || "—"}</p>
                  </div>
                )}
                {isAdmin && detailFilm.emby_id && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-mono text-[10px]">Emby ID: {detailFilm.emby_id}</span>
                  </div>
                )}
                {isAdmin && detailFilm.percorso_file && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <FolderOpen className="h-4 w-4 shrink-0" />
                    <span className="break-all">{detailFilm.percorso_file}</span>
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {confirmDuplicate && (
        <Dialog open={!!confirmDuplicate} onOpenChange={() => setConfirmDuplicate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Film già presente</DialogTitle>
              <DialogDescription>
                Un film con questo ID TMDB esiste già nella collezione. Vuoi procedere comunque?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDuplicate(null)}>
                Annulla
              </Button>
              <Button onClick={() => forceSave(confirmDuplicate)}>Procedi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {missingTmdbOpen && (
        <Dialog open={missingTmdbOpen} onOpenChange={setMissingTmdbOpen}>
          <DialogContent className="max-h-[80vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>Film senza TMDB ID</DialogTitle>
              <DialogDescription>
                {missingTmdbList.length} film con emby_id ma senza tmdb_id nel database — clicca un titolo per aprirlo nel catalogo
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-2">
              {missingTmdbList.map((f, idx) => {
                const catalogFilm = allFilms.find(
                  (cf) => cf.emby_id != null && String(cf.emby_id) === String(f.emby_id)
                );
                return (
                  <div key={idx} className="flex justify-between items-center text-sm border-b border-border pb-1 pt-1 gap-2">
                    <div className="flex flex-col min-w-0">
                      {catalogFilm ? (
                        <button
                          className="font-medium text-primary hover:underline text-left cursor-pointer inline-flex items-center gap-1 min-w-0"
                          title="Apri questo titolo nel catalogo"
                          onClick={() => {
                            setMissingTmdbOpen(false);
                            setReturnToMissingTmdb(true);
                            setDetailFilm(catalogFilm);
                          }}
                        >
                          <span className="truncate">{f.titolo}</span>
                          <ExternalLink className="h-3 w-3 shrink-0" />
                        </button>
                      ) : (
                        <span className="font-medium">{f.titolo}</span>
                      )}
                      {f.categoria && <span className="text-[10px] text-muted-foreground">{f.categoria}</span>}
                    </div>
                    <span className="text-[10px] text-muted-foreground font-mono shrink-0">{f.emby_id}</span>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {filesSizeOpen && (
        <Dialog open={filesSizeOpen} onOpenChange={setFilesSizeOpen}>
          <DialogContent className="max-h-[85vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>File per dimensione</DialogTitle>
              <DialogDescription>
                {filesSizeLoading
                  ? "Interrogo Emby e calcolo le dimensioni..."
                  : filesSizeData
                  ? `${filesSizeFiltered.length} file — ${formatGB(filesSizeTotalBytes)} occupati — clicca un titolo per aprirlo nel catalogo`
                  : "Nessun dato"}
              </DialogDescription>
            </DialogHeader>
            {filesSizeData && !filesSizeLoading && (
              <>
                <div className="flex flex-wrap items-center gap-2 pb-1">
                  <Label htmlFor="files-size-drive" className="text-xs text-muted-foreground shrink-0">
                    Unità
                  </Label>
                  <select
                    id="files-size-drive"
                    className="rounded border bg-card px-2 py-1 text-sm flex-1 min-w-32"
                    value={filesSizeDrive}
                    onChange={(e) => {
                      setFilesSizeDrive(e.target.value);
                      setFilesSizeCount(200);
                    }}
                  >
                    <option value="">Tutte le unità ({filesSizeData.count})</option>
                    {(filesSizeData.drives || []).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFilesSizeDir((dir) => (dir === "desc" ? "asc" : "desc"))}
                    title={filesSizeDir === "desc" ? "Dalla più pesante alla più leggera — clicca per invertire" : "Dalla più leggera alla più pesante — clicca per invertire"}
                  >
                    <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
                    {filesSizeDir === "desc" ? "Dalla più pesante" : "Dalla più leggera"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchFilesBySize(true)}
                    disabled={filesSizeLoading}
                    title="Ricalcola da Emby (ignora i dati in cache)"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${filesSizeLoading ? "animate-spin" : ""}`} />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportFilesSizeTxt}
                    disabled={!filesSizeData || filesSizeFiltered.length === 0}
                    title="Scarica un file .txt con l'elenco attuale (unità e ordine scelti)"
                  >
                    <Download className="h-3.5 w-3.5 mr-1" />
                    Esporta TXT
                  </Button>
                </div>
                <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-2">
                  {filesSizeFiltered.slice(0, filesSizeCount).map((f, idx) => {
                    const catalogFilm = allFilms.find(
                      (cf) => cf.emby_id != null && String(cf.emby_id) === String(f.emby_id)
                    );
                    return (
                      <div key={f.percorso_file} className="flex justify-between items-center text-sm border-b border-border pb-1 pt-1 gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[10px] text-muted-foreground font-mono w-7 shrink-0 text-right">
                            {idx + 1}
                          </span>
                          <div className="flex flex-col min-w-0">
                            {catalogFilm ? (
                              <button
                                className="font-medium text-primary hover:underline text-left cursor-pointer inline-flex items-center gap-1 min-w-0"
                                title="Apri questo titolo nel catalogo"
                                onClick={() => {
                                  setFilesSizeOpen(false);
                                  setDetailFilm(catalogFilm);
                                }}
                              >
                                <span className="truncate">{f.titolo}</span>
                                <ExternalLink className="h-3 w-3 shrink-0" />
                              </button>
                            ) : (
                              <span className="font-medium truncate">{f.titolo}</span>
                            )}
                            <span className="text-[10px] text-muted-foreground truncate" title={f.percorso_file}>
                              {f.drive ? `${f.drive} · ` : ""}{f.categoria}
                              {f.risoluzione ? ` · ${f.risoluzione}` : ""}
                            </span>
                          </div>
                        </div>
                        <span className="font-semibold shrink-0 whitespace-nowrap">{formatGB(f.size_bytes)}</span>
                      </div>
                    );
                  })}
                  {filesSizeFiltered.length === 0 && (
                    <div className="text-sm text-muted-foreground py-4">Nessun file trovato per questa unità.</div>
                  )}
                </div>
                {filesSizeFiltered.length > filesSizeCount && (
                  <div className="pt-2">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setFilesSizeCount((c) => c + 200)}
                    >
                      Carica altri ({filesSizeFiltered.length - filesSizeCount} rimanenti)
                    </Button>
                  </div>
                )}
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {duplicateOpen && (
        <Dialog open={duplicateOpen} onOpenChange={setDuplicateOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-4xl">
            <DialogHeader>
              <DialogTitle>Duplicati nel catalogo</DialogTitle>
              <DialogDescription>
                {duplicateData ? `${duplicateData.total_groups} gruppi di duplicati trovati` : "Raggruppa per ID nel nome file (es. [tmdbid=12345]) — solo vere copie identiche."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Categoria:</label>
                <select
                  className="rounded border bg-card px-2 py-1 text-sm"
                  value={duplicateCategory}
                  onChange={(e) => {
                    setDuplicateCategory(e.target.value);
                    setDuplicateSearched(false);
                    setDuplicateData(null);
                  }}
                >
                  <option value="">Tutte le categorie</option>
                  {CATEGORIE.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => {
                    setDuplicateSearched(true);
                    fetchDuplicates(duplicateCategory);
                  }}
                  disabled={duplicateLoading}
                >
                  {duplicateLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Avvia ricerca
                </Button>
              </div>

              {!duplicateSearched && (
                <div className="text-center text-muted-foreground py-8">
                  Seleziona una categoria e clicca <strong>Avvia ricerca</strong> per iniziare.
                </div>
              )}

              {duplicateSearched && duplicateData && duplicateData.total_groups === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Nessun duplicato trovato.
                </div>
              )}

              {duplicateSearched && duplicateData && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] uppercase font-semibold text-muted-foreground mr-1">Ordine</span>
                    <button
                      onClick={() => setDuplicateSort("az")}
                      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${duplicateSort === "az" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                      title="Ordine A-Z"
                    >
                      A-Z
                    </button>
                    <button
                      onClick={() => setDuplicateSort("za")}
                      className={`rounded px-2 py-1 text-xs font-medium transition-colors ${duplicateSort === "za" ? "bg-primary text-primary-foreground" : "bg-muted hover:bg-accent text-muted-foreground"}`}
                      title="Ordine Z-A"
                    >
                      Z-A
                    </button>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">Gruppi</div>
                    <div className="text-lg font-bold text-primary leading-none">{duplicateData.total_groups}</div>
                  </div>
                </div>
              )}

              {duplicateSearched && duplicateData && [...duplicateData.duplicates].sort((a, b) => {
                const ta = (a.films[0]?.titolo || "").toLowerCase();
                const tb = (b.films[0]?.titolo || "").toLowerCase();
                if (duplicateSort === "az") return ta.localeCompare(tb, "it", { sensitivity: "base" });
                return tb.localeCompare(ta, "it", { sensitivity: "base" });
              }).map((group) => (
                <div key={group.provider_id} className="rounded-lg border bg-card p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-sm">{group.films[0]?.titolo || "Senza titolo"}</h4>
                      <p className="text-[10px] text-muted-foreground font-mono">{group.provider_id}</p>
                    </div>
                    <Badge variant="secondary">{group.count} copie</Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {group.films.map((film) => (
                      <div key={film.id} className="flex items-start gap-2 rounded border p-2 text-xs">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{film.titolo}</p>
                          <p className="text-muted-foreground">Anno: {film.anno || "N/A"}</p>
                          <p className="text-muted-foreground truncate">Cat: {film.categoria || "N/A"}</p>
                          <p className="text-muted-foreground truncate" title={film.percorso_file}>Path: {film.percorso_file || "N/A"}</p>
                          {film.emby_id && <p className="text-muted-foreground">Emby: {film.emby_id}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicateOpen(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {nonStandardOpen && (
        <Dialog open={nonStandardOpen} onOpenChange={setNonStandardOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-6xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>File con estensione non standard</DialogTitle>
              <DialogDescription>
                {nonStandardSearched && nonStandardData ? `${nonStandardData.count} film con estensione diversa da .mkv e .mp4` : "Ricerca sui file fisici di Emby (ignora cartelle collezione)."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Categoria:</label>
                <select
                  className="rounded border bg-card px-2 py-1 text-sm"
                  value={nonStandardCategory}
                  onChange={(e) => {
                    setNonStandardCategory(e.target.value);
                    setNonStandardSearched(false);
                    setNonStandardData(null);
                  }}
                >
                  <option value="">Tutte le categorie</option>
                  {CATEGORIE.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={async () => {
                    setNonStandardSearched(true);
                    setNonStandardLoading(true);
                    try {
                      const catParam = nonStandardCategory ? `?categoria=${encodeURIComponent(nonStandardCategory)}` : "";
                      const { data } = await api.get(`/api/admin/non-standard-files${catParam}`);
                      setNonStandardData(data);
                    } catch (err) {
                      toast.error("Errore nel caricamento file non standard");
                    } finally {
                      setNonStandardLoading(false);
                    }
                  }}
                  disabled={nonStandardLoading}
                >
                  {nonStandardLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Avvia ricerca
                </Button>
              </div>

              {!nonStandardSearched && (
                <div className="text-center text-muted-foreground py-8">
                  Seleziona una categoria e clicca <strong>Avvia ricerca</strong> per iniziare.
                </div>
              )}

              {nonStandardSearched && nonStandardLoading && (
                <div className="text-center text-muted-foreground py-8">
                  <FilmStripLoader className="mx-auto mb-2 h-6 w-6" />
                  Caricamento...
                </div>
              )}

              {nonStandardSearched && nonStandardData && nonStandardData.count === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Nessun file non standard trovato. Tutti i film sono .mkv o .mp4.
                </div>
              )}

              {nonStandardSearched && nonStandardData && nonStandardData.count > 0 && (
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">Trovati</div>
                    <div className="text-lg font-bold text-primary leading-none">{nonStandardData.count}</div>
                  </div>
                </div>
              )}

              {nonStandardSearched && nonStandardData && nonStandardData.films.map((f) => (
                <div key={f.id} className="flex justify-between items-center text-sm border-b border-border pb-1 pt-1">
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{f.titolo}</span>
                    <span className="text-[10px] text-muted-foreground truncate" title={f.percorso_file}>{f.percorso_file}</span>
                    {f.categoria && <span className="text-[10px] text-muted-foreground">{f.categoria}</span>}
                  </div>
                  <Badge variant="secondary" className="shrink-0 ml-2">{f.estensione || "?"}</Badge>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNonStandardOpen(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {noCoverOpen && (
        <Dialog open={noCoverOpen} onOpenChange={setNoCoverOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-6xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>Film senza copertina</DialogTitle>
              <DialogDescription>
                {noCoverSearched && noCoverData ? `${noCoverData.count} film privi di copertina` : "Trova i record nel DB che non hanno alcuna immagine associata."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Categoria:</label>
                <select
                  className="rounded border bg-card px-2 py-1 text-sm"
                  value={noCoverCategory}
                  onChange={(e) => {
                    setNoCoverCategory(e.target.value);
                    setNoCoverSearched(false);
                    setNoCoverData(null);
                  }}
                >
                  <option value="">Tutte le categorie</option>
                  {CATEGORIE.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={async () => {
                    setNoCoverSearched(true);
                    setNoCoverLoading(true);
                    try {
                      const catParam = noCoverCategory ? `?categoria=${encodeURIComponent(noCoverCategory)}` : "";
                      const { data } = await api.get(`/api/admin/no-cover${catParam}`);
                      setNoCoverData(data);
                    } catch (err) {
                      toast.error("Errore nel caricamento film senza copertina");
                    } finally {
                      setNoCoverLoading(false);
                    }
                  }}
                  disabled={noCoverLoading}
                >
                  {noCoverLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Avvia ricerca
                </Button>
              </div>

              {!noCoverSearched && (
                <div className="text-center text-muted-foreground py-8">
                  Seleziona una categoria e clicca <strong>Avvia ricerca</strong> per iniziare.
                </div>
              )}

              {noCoverSearched && noCoverLoading && (
                <div className="text-center text-muted-foreground py-8">
                  <FilmStripLoader className="mx-auto mb-2 h-6 w-6" />
                  Caricamento...
                </div>
              )}

              {noCoverSearched && noCoverData && noCoverData.count === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Nessun film senza copertina trovato.
                </div>
              )}

              {noCoverSearched && noCoverData && noCoverData.count > 0 && (
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">Trovati</div>
                    <div className="text-lg font-bold text-primary leading-none">{noCoverData.count}</div>
                  </div>
                </div>
              )}

              {noCoverSearched && noCoverData && noCoverData.films.map((f) => (
                <div key={f.id} className="flex justify-between items-center text-sm border-b border-border pb-1 pt-1">
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium truncate">{f.titolo}</span>
                    {f.anno && <span className="text-[10px] text-muted-foreground">Anno: {f.anno}</span>}
                    {f.categoria && <span className="text-[10px] text-muted-foreground">{f.categoria}</span>}
                  </div>
                  <Badge variant="secondary" className="shrink-0 ml-2">{f.motivo}</Badge>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoCoverOpen(false)}>Chiudi</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {activeUsersOpen && (
        <Dialog open={activeUsersOpen} onOpenChange={setActiveUsersOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-3xl w-[95vw]">
            <DialogHeader>
              <DialogTitle>Utenti attivi</DialogTitle>
              <DialogDescription>
                Sessioni attive su Emby (auto-refresh ogni 30s)
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-4">
              {activeUsersLoading && !activeUsersData && (
                <div className="text-center text-muted-foreground py-8">
                  Caricamento...
                </div>
              )}

              {activeUsersData && activeUsersData.count === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Nessuno sta guardando al momento.
                </div>
              )}

              {activeUsersData && activeUsersData.count > 0 && (
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wide">Attivi</div>
                    <div className="text-lg font-bold text-primary leading-none">{activeUsersData.count}</div>
                  </div>
                </div>
              )}

              {activeUsersData && activeUsersData.users.map((u, i) => (
                <div key={i} className="flex flex-col gap-1 text-sm border-b border-border pb-2 pt-1">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{u.user_name}</span>
                    <span className="text-[10px] text-muted-foreground">{u.device}</span>
                  </div>
                  <div className="text-[11px] text-primary">
                    <strong>{u.playing.paused ? "In pausa: " : "Sta guardando: "}</strong>
                    {u.playing.title}{u.playing.year ? ` (${u.playing.year})` : ""}
                    {u.playing.type && <span className="text-muted-foreground ml-1">({u.playing.type})</span>}
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setActiveUsersOpen(false)}>Chiudi</Button>
              <Button variant="outline" onClick={fetchActiveUsers} disabled={activeUsersLoading}>
                {activeUsersLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                Aggiorna
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {historyOpen && (
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-3xl w-[95vw] overflow-x-hidden">
            <DialogHeader>
              <DialogTitle>Storico visioni</DialogTitle>
              <DialogDescription>
                Seleziona un utente Emby per vedere gli ultimi titoli che ha guardato.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Utente</Label>
                <select
                  className="rounded border bg-background px-3 py-2 text-sm"
                  value={historySelectedUser}
                  onChange={(e) => {
                    const uid = e.target.value;
                    setHistorySelectedUser(uid);
                    setHistoryData(null);
                    setHistoryOffset(0);
                    if (uid) fetchHistoryData(0, uid);
                  }}
                >
                  <option value="">Seleziona un utente</option>
                  {historyUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </div>

              {historySelectedUser && (
                <div className="rounded border bg-muted/30 p-3">
                  {usersActivityLoading ? (
                    <div className="text-center text-muted-foreground py-4">Caricamento attività...</div>
                  ) : usersActivity.length === 0 ? (
                    <div className="text-center text-muted-foreground py-4">Nessun dato di attività disponibile.</div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                      <div className="flex flex-col items-center gap-2">
                        <svg viewBox="0 0 40 40" className="h-28 w-28 -rotate-90">
                          <circle cx="20" cy="20" r="15.9155" fill="none" stroke="currentColor" strokeWidth="6" className="text-muted-foreground/30" />
                          <circle
                            cx="20"
                            cy="20"
                            r="15.9155"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="6"
                            className="text-blue-500"
                            strokeDasharray={`${activityPercent} ${100 - activityPercent}`}
                          />
                        </svg>
                        <div className="text-sm font-medium text-center">
                          {selectedUserName}: {userActivityCount} titoli ({activityPercent.toFixed(1)}%)
                        </div>
                        <div className="text-xs text-muted-foreground">Totale visioni tutti gli utenti: {totalActivityCount}</div>
                      </div>
                      <div className="grid gap-1.5 max-h-44 overflow-y-auto">
                        {usersActivity.map((u) => (
                          <div
                            key={u.id}
                            className={`flex justify-between items-center text-xs px-2 py-1 rounded ${u.id === historySelectedUser ? "bg-blue-500/10 font-medium text-blue-700 dark:text-blue-300" : ""}`}
                          >
                            <span className="truncate mr-2">{u.name}</span>
                            <span className="shrink-0 font-semibold">{u.count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {historyLoading && (
                <div className="text-center text-muted-foreground py-8">Caricamento...</div>
              )}

              {!historyLoading && historyData && historyData.items.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  Nessun titolo trovato nello storico.
                </div>
              )}

              {!historyLoading && historyData && historyData.items.length > 0 && (
                <>
                  <div className="text-xs text-muted-foreground">
                    Visualizzati {historyOffset + 1}-{Math.min(historyOffset + HISTORY_LIMIT, historyData.total)} di {historyData.total}
                  </div>
                  <div className="grid gap-2">
                    {historyData.items.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm border-b border-border pb-2 pt-1">
                        {item.copertina_url ? (
                          <img src={item.copertina_url} alt="" className="h-12 w-8 object-cover rounded" />
                        ) : (
                          <div className="h-12 w-8 bg-muted rounded flex items-center justify-center text-[10px] text-muted-foreground">N/A</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{item.titolo}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {item.anno ? `(${item.anno}) ` : ""}
                            {item.tipo === "Episode" ? "Episodio" : "Film"}
                          </div>
                        </div>
                        <div className="text-[11px] text-right shrink-0 flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {item.data_visione ? new Date(item.data_visione).toLocaleString("it-IT") : "-"}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryOpen(false)}>Chiudi</Button>
              <Button
                variant="outline"
                onClick={() => fetchHistoryData(historyOffset - HISTORY_LIMIT)}
                disabled={historyLoading || historyOffset <= 0}
              >
                Precedente
              </Button>
              <Button
                variant="outline"
                onClick={() => fetchHistoryData(historyOffset + HISTORY_LIMIT)}
                disabled={historyLoading || !historyData || historyOffset + HISTORY_LIMIT >= historyData.total}
              >
                Successivo
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {embyOpen && (
        <Dialog open={embyOpen} onOpenChange={setEmbyOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-7xl w-[98vw] overflow-x-hidden">
            <DialogHeader>
              <DialogTitle>Importa da Emby</DialogTitle>
              <DialogDescription>
                Seleziona una libreria Emby da importare. La categoria verrà determinata automaticamente dal nome della libreria.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runEmbyDiagnose}
                  disabled={embyDiagnosing}
                >
                  {embyDiagnosing ? "Test in corso..." : "Test connessione Emby"}
                </Button>
                <span className="text-xs text-muted-foreground">
                  Verifica che il backend riesca a raggiungere Emby
                </span>
              </div>
              {embyDiagnoseResult && (
                <div className={`rounded border p-3 text-sm ${embyDiagnoseResult.ok ? "border-green-500/30 bg-green-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                  <p className="font-medium">{embyDiagnoseResult.ok ? "Connessione OK" : "Problema rilevato"}</p>
                  {embyDiagnoseResult.error && (
                    <p className="text-muted-foreground mt-1">{embyDiagnoseResult.error}</p>
                  )}
                  {embyDiagnoseResult.system_info_public && (
                    <p className="text-xs text-muted-foreground mt-1">
                      System/Public HTTP {embyDiagnoseResult.system_info_public.status_code}
                    </p>
                  )}
                  {embyDiagnoseResult.system_info && (
                    <p className="text-xs text-muted-foreground mt-1">
                      System/Info HTTP {embyDiagnoseResult.system_info.status_code}
                    </p>
                  )}
                </div>
              )}
              <div className="grid gap-2">
                <Label>Libreria Emby</Label>
                <select
                  className="rounded border bg-background px-3 py-2 text-sm"
                  value={embySelectedLibrary}
                  onChange={(e) => setEmbySelectedLibrary(e.target.value)}
                >
                  {embyLibraries.length === 0 && (
                    <option value="">Nessuna libreria trovata</option>
                  )}
                  {embyLibraries.map((lib) => (
                    <option key={lib.id} value={lib.id}>
                      {lib.name}
                    </option>
                  ))}
                </select>
              </div>

              {embyResult && (
                <div className="rounded bg-muted p-3 text-sm space-y-3">
                  <div>
                    <p className="font-medium">Importazione completata</p>
                    <p className="text-muted-foreground">
                      Importati: {embyResult.imported} · Saltati: {embyResult.skipped} · Ignorati: {embyResult.ignored || 0} · Totali: {embyResult.total}
                    </p>
                  </div>

                  {/* SEZIONE 1: Film appena importati — SEMPRE IN TESTA */}
                  {(embyResult.newly_imported || []).length > 0 ? (
                    <div className="rounded border border-green-500/30 bg-green-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-green-700 dark:text-green-400 mb-2 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                        Film appena importati ({(embyResult.newly_imported || []).length})
                      </p>
                      <ul className="max-h-40 overflow-y-auto space-y-1 rounded bg-background p-2 text-xs">
                        {(embyResult.newly_imported || []).map((item, idx) => (
                          <li key={idx} className="flex items-center justify-between gap-2">
                            <span className="truncate font-medium text-green-900 dark:text-green-100">{item.titolo}{item.anno ? ` (${item.anno})` : ""}</span>
                            {item.provider_id && <span className="shrink-0 text-muted-foreground">[{item.provider_id}]</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : embyResult.imported > 0 ? (
                    <div className="rounded border border-green-500/30 bg-green-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-green-700 dark:text-green-400 mb-1 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
                        Film appena importati ({embyResult.imported})
                      </p>
                      <p className="text-xs text-green-800 dark:text-green-300">
                        {embyResult.imported} film importato/i con successo.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded border border-green-500/20 bg-green-500/5 p-2 text-xs text-green-800 dark:text-green-300">
                      Nessun film nuovo importato in questa sessione.
                    </div>
                  )}

                  {/* SEZIONE 2: Film già presenti — DOPO */}
                  {(embyResult.already_present || embyResult.skipped_items || []).length > 0 ? (
                    <div className="rounded border border-orange-500/30 bg-orange-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-orange-700 dark:text-orange-400 mb-2 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                        Film già presenti ({(embyResult.already_present || embyResult.skipped_items || []).length})
                      </p>
                      <ul className="max-h-40 overflow-y-auto space-y-1 rounded bg-background p-2 text-xs">
                        {(embyResult.already_present || embyResult.skipped_items || []).map((item, idx) => (
                          <li key={idx} className="flex items-center justify-between gap-2">
                            <span className="truncate text-orange-900 dark:text-orange-100">{item.titolo}</span>
                            <span className="shrink-0 text-muted-foreground">({item.motivo})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : embyResult.skipped > 0 ? (
                    <div className="rounded border border-orange-500/30 bg-orange-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-orange-700 dark:text-orange-400 mb-1 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-orange-500" />
                        Film già presenti ({embyResult.skipped})
                      </p>
                      <p className="text-xs text-orange-800 dark:text-orange-300">
                        {embyResult.skipped} film già presente/i nel database.
                      </p>
                    </div>
                  ) : (
                    <div className="rounded border border-orange-500/20 bg-orange-500/5 p-2 text-xs text-orange-800 dark:text-orange-300">
                      Nessun film già presente saltato.
                    </div>
                  )}

                  {/* SEZIONE 3: Film ignorati (senza titolo) */}
                  {(embyResult.ignored_items || []).length > 0 ? (
                    <div className="rounded border border-gray-500/30 bg-gray-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-gray-700 dark:text-gray-400 mb-2 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-gray-500" />
                        Film ignorati ({(embyResult.ignored_items || []).length})
                      </p>
                      <ul className="max-h-40 overflow-y-auto space-y-1 rounded bg-background p-2 text-xs">
                        {(embyResult.ignored_items || []).map((item, idx) => (
                          <li key={idx} className="flex items-center justify-between gap-2">
                            <span className="truncate text-gray-900 dark:text-gray-100">{item.titolo}</span>
                            <span className="shrink-0 text-muted-foreground">({item.motivo})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : embyResult.ignored > 0 ? (
                    <div className="rounded border border-gray-500/30 bg-gray-500/10 p-2">
                      <p className="text-xs font-bold uppercase text-gray-700 dark:text-gray-400 mb-1 flex items-center gap-1">
                        <span className="inline-block w-2 h-2 rounded-full bg-gray-500" />
                        Film ignorati ({embyResult.ignored})
                      </p>
                      <p className="text-xs text-gray-800 dark:text-gray-300">
                        {embyResult.ignored} film saltato/i perché privi di titolo.
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmbyOpen(false)}>
                Chiudi
              </Button>
              <Button onClick={handleEmbyImport} disabled={embyImporting || !embySelectedLibrary}>
                {embyImporting ? "Importazione..." : "Importa"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {syncOpen && (
        <Dialog open={syncOpen} onOpenChange={setSyncOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md w-[98vw] overflow-x-hidden">
            <DialogHeader>
              <DialogTitle>Sincronizza con Emby</DialogTitle>
              <DialogDescription>
                Seleziona una categoria da sincronizzare, oppure "Tutte le categorie" per sincronizzare l'intero catalogo.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Categoria</Label>
                <select
                  className="rounded border bg-background px-3 py-2 text-sm"
                  value={syncSelectedCategoria}
                  onChange={(e) => setSyncSelectedCategoria(e.target.value)}
                >
                  <option value="">Tutte le categorie</option>
                  {CATEGORIE.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSyncOpen(false)}>
                Annulla
              </Button>
              <Button onClick={() => syncLibrary(syncSelectedCategoria || null)} disabled={syncing}>
                {syncing ? "Sincronizzazione..." : "Sincronizza"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {requestOpen && (
        <Dialog open={requestOpen} onOpenChange={(open) => { setRequestOpen(open); if (!open) setRequestSuccess(false); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            {requestSuccess ? (
              <>
                <DialogHeader className="shrink-0">
                  <DialogTitle>
                    {lastSubmittedCount > 1 ? `${lastSubmittedCount} richieste inviate` : "Richiesta inviata"}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto min-h-0 text-center py-2">
                  <div className="mb-4 text-lg font-semibold text-green-700">
                    {lastSubmittedCount > 1
                      ? `Le tue ${lastSubmittedCount} richieste sono state inoltrate correttamente`
                      : "La tua richiesta è stata inoltrata correttamente"}
                  </div>
                  <div className="mb-6 text-sm text-muted-foreground">
                    Verrai contattato/a via Whatsapp dall'amministratore
                  </div>
                  {myRequests.length > 0 && (
                    <>
                      <div className="text-lg font-semibold leading-none tracking-tight text-center py-2">
                        Le tue richieste
                      </div>
                      <div className="text-sm text-muted-foreground text-center pb-2">
                        Controlla lo stato delle tue richieste e le risposte
                      </div>
                      <div className="rounded border bg-muted/30 p-3 mb-4 text-left mx-6">
                        <ul className="space-y-2 max-h-40 overflow-y-auto">
                        {myRequests.map((r) => (
                          <li key={r.id} className={`flex flex-col gap-1 text-sm rounded border p-2 ${r.risposta_vista === false ? 'border-red-300 bg-red-50/40' : 'border-transparent'}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="truncate font-medium flex items-center gap-1">
                                  {r.titolo}
                                  {r.risposta_vista === false && <span className="inline-block h-2 w-2 rounded-full bg-red-500" title="Nuova risposta" />}
                                </div>
                                {r.anno && <span className="text-xs text-muted-foreground">({r.anno})</span>}
                                {r.note && <div className="text-xs text-muted-foreground truncate">{r.note}</div>}
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-[10px] text-muted-foreground">
                                  {new Date(r.data_richiesta).toLocaleDateString("it-IT")}
                                </div>
                              </div>
                            </div>
                            {r.risposta && (
                              <div className="rounded bg-green-50 border border-green-200 p-1.5 text-xs text-green-800">
                                <span className="font-semibold">Risposta:</span> {r.risposta}
                              </div>
                            )}
                          </li>
                        ))}
                        </ul>
                      </div>
                    </>
                  )}
                  <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mb-4 mx-6">
                    📸 Ti consigliamo di fare uno screenshot per annotarti quanto indicato.
                  </div>
                </div>
                <div className="shrink-0 pt-2 pb-6 text-center">
                  <Button onClick={() => setRequestOpen(false)}>Chiudi</Button>
                </div>
              </>
            ) : (
              <>
                <DialogHeader className="shrink-0">
                  <DialogTitle>Richiesta film o serie</DialogTitle>
                  <DialogDescription>
                    Chiedi uno o più titoli da aggiungere al catalogo.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto min-h-0 grid gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="req-nome">Il tuo nome *</Label>
                    <Input
                      id="req-nome"
                      placeholder="Mario"
                      value={requestForm.nome_utente}
                      onChange={(e) =>
                        setRequestForm((f) => ({ ...f, nome_utente: e.target.value }))
                      }
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Cosa vuoi richiedere?</Label>
                    <div className="flex gap-2 flex-wrap">
                      {Object.entries(TIPO_RICHIESTA_META).map(([key, meta]) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setRequestTipo(key)}
                          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                            requestTipo === key
                              ? "bg-primary text-primary-foreground border-transparent"
                              : "border-input bg-background hover:bg-accent hover:text-accent-foreground"
                          }`}
                        >
                          {meta.icon} {meta.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label>{TIPO_RICHIESTA_META[requestTipo].fieldLabel}</Label>
                    {requestItems.map((item, idx) => (
                      <div key={idx} className="grid gap-1">
                        <div className="flex gap-2 items-start">
                          <div className="flex-1 min-w-0">
                            <Input
                              placeholder={TIPO_RICHIESTA_META[requestTipo].placeholder}
                              value={item.titolo}
                              onChange={(e) => updateRequestItem(idx, "titolo", e.target.value)}
                            />
                          </div>
                          <Input
                            type="number"
                            placeholder="Anno"
                            className="w-20 shrink-0"
                            value={item.anno}
                            onChange={(e) => updateRequestItem(idx, "anno", e.target.value)}
                          />
                          {requestItems.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRequestItem(idx)}
                              title="Rimuovi titolo"
                              className="shrink-0 inline-flex h-10 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        {requestCheckLoadingMap[idx] && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" /> Verifica catalogo...
                          </div>
                        )}
                        {requestChecks[idx]?.exists && (
                          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">
                            <div className="mb-1 flex items-center gap-1 font-semibold">
                              <AlertTriangle className="h-3 w-3" /> Attenzione: potrebbe essere già presente
                            </div>
                            <ul className="space-y-1 max-h-32 overflow-y-auto pr-1">
                              {requestChecks[idx].matches.map((m, mi) => (
                                <li key={mi} className="flex items-center gap-1 flex-wrap">
                                  <span className="font-medium">{m.titolo}</span>
                                  {m.anno && <span className="text-amber-600">({m.anno})</span>}
                                  {m.categoria && <Badge variant="secondary" className="text-[10px]">{m.categoria}</Badge>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                    {requestItems.length < 10 && (
                      <button
                        type="button"
                        onClick={addRequestItem}
                        className="w-full inline-flex items-center justify-center gap-1 rounded-md border border-dashed py-2 text-sm text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
                      >
                        + Aggiungi altro titolo
                      </button>
                    )}
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="req-note">Note (facoltative, valgono per tutti i titoli)</Label>
                    <Textarea
                      id="req-note"
                      rows={2}
                      placeholder="Scrivi qui eventuali note o dettagli utili sulla tua richiesta (facoltativo)"
                      value={requestForm.note}
                      onChange={(e) =>
                        setRequestForm((f) => ({ ...f, note: e.target.value }))
                      }
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setRequestOpen(false)}>
                      Annulla
                    </Button>
                    <Button
                      onClick={submitRequest}
                      disabled={requestSubmitting || validRequestCount === 0 || !requestForm.nome_utente.trim()}
                    >
                      {requestSubmitting ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {requestSubmitting
                        ? "Invio..."
                        : validRequestCount > 1
                        ? `Invia ${validRequestCount} richieste`
                        : "Invia richiesta"}
                    </Button>
                  </DialogFooter>
                {myRequests.length > 0 && (
                  <div className="mt-2">
                    <button
                      onClick={() => setShowMyRequestsDropdown(!showMyRequestsDropdown)}
                      className="w-full flex items-center justify-between rounded border border-red-300 bg-muted/30 px-3 py-2 text-sm font-medium hover:bg-muted/50 transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        📬 Le tue richieste ({myRequests.length})
                        {myRequests.some((r) => r.risposta_vista === false) && (
                          <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold text-white">
                            {myRequests.filter((r) => r.risposta_vista === false).length}
                          </span>
                        )}
                      </span>
                      <span>{showMyRequestsDropdown ? '▲' : '▼'}</span>
                    </button>
                    {showMyRequestsDropdown && (
                      <div className="rounded border border-red-300 bg-muted/30 p-3 mt-1">
                        <div className="text-sm text-muted-foreground text-center pb-2">
                          Controlla lo stato delle tue richieste e le risposte
                        </div>
                        <ul className="space-y-2 max-h-40 overflow-y-auto">
                        {myRequests.map((r) => (
                          <li key={r.id} className={`flex flex-col gap-1 text-sm rounded border p-2 ${r.risposta_vista === false ? 'border-red-300 bg-red-50/40' : 'border-transparent'}`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="truncate font-medium flex items-center gap-1">
                                  {r.titolo}
                                  {r.risposta_vista === false && <span className="inline-block h-2 w-2 rounded-full bg-red-500" title="Nuova risposta" />}
                                </div>
                                {r.anno && <span className="text-xs text-muted-foreground">({r.anno})</span>}
                                {r.note && <div className="text-xs text-muted-foreground truncate">{r.note}</div>}
                              </div>
                              <div className="shrink-0 text-right">
                                <div className="text-[10px] text-muted-foreground">
                                  {new Date(r.data_richiesta).toLocaleDateString("it-IT")}
                                </div>
                              </div>
                            </div>
                            {r.risposta && (
                              <div className="rounded bg-green-50 border border-green-200 p-1.5 text-xs text-green-800">
                                <span className="font-semibold">Risposta:</span> {r.risposta}
                              </div>
                            )}
                          </li>
                        ))}
                        </ul>
                      </div>
                    )}
                    {!showMyRequestsDropdown && (
                      <div className="text-xs text-muted-foreground text-center pt-1">
                        clicca per vedere lo stato delle tue richieste
                      </div>
                    )}
                  </div>
                )}
                </div>
              </>
            )}
            {/* Popup "novità modulo" sovrapposto al modulo, con "Non mostrare più" */}
            {requestNewsOpen && (
              <Dialog open={requestNewsOpen} onOpenChange={(o) => { if (!o) closeRequestNews(false); }}>
                <DialogContent className="max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Il modulo richieste è rinnovato ✨</DialogTitle>
                    <DialogDescription>
                      Piccole novità per farti risparmiare tempo
                    </DialogDescription>
                  </DialogHeader>
                  <ul className="space-y-2 text-sm">
                    <li>
                      🎬📺💬 <b>Scegli il tipo</b>: Film, Serie TV o Altro.
                    </li>
                    <li>
                      ➕ <b>Chiedi più titoli in una volta sola</b>: ogni titolo arriva come
                      richiesta separata, così l'amministratore li gestisce più in fretta.
                    </li>
                    <li>
                      📝 Nome e note si scrivono <b>una sola volta</b> e valgono per tutti i titoli.
                    </li>
                  </ul>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => closeRequestNews(true)}>
                      Non mostrare più
                    </Button>
                    <Button onClick={() => closeRequestNews(false)}>Ho capito</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </DialogContent>
        </Dialog>
      )}

      {reportOpen && (
        <Dialog open={reportOpen} onOpenChange={setReportOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Segnala un problema</DialogTitle>
              <DialogDescription>
                Segnala doppioni, locandine mancanti o qualsiasi altro problema nel catalogo.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="rep-nome">Il tuo nome *</Label>
                <Input
                  id="rep-nome"
                  placeholder="Mario"
                  value={reportForm.nome_utente}
                  onChange={(e) =>
                    setReportForm((f) => ({ ...f, nome_utente: e.target.value }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="rep-testo">Descrizione del problema *</Label>
                <Textarea
                  id="rep-testo"
                  rows={4}
                  placeholder="Es. ci sono doppioni di film, manca la locandina, il titolo è sbagliato..."
                  value={reportForm.testo}
                  onChange={(e) =>
                    setReportForm((f) => ({ ...f, testo: e.target.value }))
                  }
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReportOpen(false)}>
                Annulla
              </Button>
              <Button onClick={submitReport} disabled={reportSubmitting}>
                {reportSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <AlertTriangle className="mr-2 h-4 w-4" />
                )}
                {reportSubmitting ? "Invio..." : "Invia segnalazione"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {adminRequestsOpen && (
        <Dialog open={adminRequestsOpen} onOpenChange={setAdminRequestsOpen}>
          <DialogContent
            ref={adminRequestsScrollRef}
            onScroll={(e) => setShowAdminBackToTop(e.currentTarget.scrollTop > 300)}
            className="max-h-[90vh] overflow-y-auto max-w-3xl"
          >
            <DialogHeader>
              <DialogTitle>Gestione Richieste</DialogTitle>
              <DialogDescription>
                {adminRequests.length} richieste ricevute
                {filteredAdminRequests.length !== adminRequests.length && ` — ${filteredAdminRequests.length} filtrate`}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-center gap-2 pb-1">
              <Label htmlFor="admin-req-user-filter" className="text-xs text-muted-foreground shrink-0">
                Utente
              </Label>
              <select
                id="admin-req-user-filter"
                className="rounded border bg-card px-2 py-1 text-sm flex-1 min-w-32"
                value={adminRequestUserFilter}
                onChange={(e) => {
                  setAdminRequestUserFilter(e.target.value);
                  setAdminRequestEditing(null);
                  setAdminVisibleCount(25);
                }}
              >
                <option value="">Tutti gli utenti ({adminRequests.length})</option>
                {adminRequestUsers.map((u) => (
                  <option key={u.nome.toLowerCase()} value={u.nome}>
                    {u.nome} ({u.count}){adminGuestKeys.has(u.nome.toLowerCase()) ? " · Guest" : ""}
                  </option>
                ))}
              </select>
              <Label htmlFor="admin-req-status-filter" className="text-xs text-muted-foreground shrink-0">
                Stato
              </Label>
              <select
                id="admin-req-status-filter"
                className="rounded border bg-card px-2 py-1 text-sm flex-1 min-w-32"
                value={adminRequestStatusFilter}
                onChange={(e) => {
                  setAdminRequestStatusFilter(e.target.value);
                  setAdminRequestEditing(null);
                  setAdminVisibleCount(25);
                }}
              >
                <option value="">Tutti gli stati ({adminRequests.length})</option>
                <option value="pending">Da gestire ({adminStatusCounts.pending})</option>
                <option value="approved">Non ancora disponibile ({adminStatusCounts.approved})</option>
                <option value="completed">Disponibile ({adminStatusCounts.completed})</option>
                <option value="visto">Viste su Emby ({adminViewCounts.visto})</option>
                <option value="non_visto">Non ancora viste ({adminViewCounts.non_visto})</option>
                <option value="chiuse">Chiuse ({adminStatusCounts.chiuse})</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchRequestViews}
                disabled={adminRequestViewsLoading}
                title="Ricontrolla le visioni su Emby"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${adminRequestViewsLoading ? "animate-spin" : ""}`} />
              </Button>
              <Button
                variant={adminGroupByUser ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  const next = !adminGroupByUser;
                  setAdminGroupByUser(next);
                  try {
                    localStorage.setItem("adminReqGroupByUser", next ? "1" : "0");
                  } catch {}
                }}
                title={adminGroupByUser ? "Vista raggruppata per utente attiva — clicca per tornare alla lista" : "Raggruppa le richieste per utente"}
              >
                <Users className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="space-y-3 py-2">
              {adminRequestsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FilmStripLoader className="h-4 w-4" /> Caricamento...
                </div>
              ) : adminRequests.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nessuna richiesta</div>
              ) : filteredAdminRequests.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nessuna richiesta per l'utente selezionato</div>
              ) : (
                adminGroupByUser ? (
                  adminGroupsByUser.map((g, gi) => {
                    const color = GROUP_COLORS[gi % GROUP_COLORS.length];
                    const isGuest = adminGuestKeys.has((g.nome || "").trim().toLowerCase());
                    return (
                      <div key={g.nome} className="rounded-lg border-2 p-3 space-y-2" style={{ borderColor: color, backgroundColor: `${color}0f` }}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="inline-block h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
                          <span className="font-semibold text-sm">{g.nome}</span>
                          <Badge variant="secondary" className="text-[10px] px-1 shrink-0">
                            {g.items.length} {g.items.length === 1 ? "richiesta" : "richieste"}
                          </Badge>
                          {isGuest && (
                            <Badge variant="secondary" className="text-[10px] px-1 shrink-0">Guest</Badge>
                          )}
                        </div>
                        {g.items.map((r) => renderAdminRequestCard(r))}
                      </div>
                    );
                  })
                ) : (
                  visibleAdminRequests.map((r) => renderAdminRequestCard(r))
                )
              )}
              {!adminRequestsLoading && filteredAdminRequests.length > adminVisibleCount && (
                <div className="pt-1">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setAdminVisibleCount((c) => c + 25)}
                  >
                    Carica altre ({filteredAdminRequests.length - adminVisibleCount} rimanenti)
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdminRequestsOpen(false)}>Chiudi</Button>
            </DialogFooter>
            {showAdminBackToTop && (
              <button
                onClick={() => {
                  const el = adminRequestsScrollRef.current;
                  if (el) el.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="sticky bottom-3 z-50 ml-auto flex h-9 w-9 items-center justify-center rounded-full bg-[#3b82f6] text-white shadow-md transition-transform hover:scale-110 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-300"
                title="Torna in alto"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
            )}
          </DialogContent>
        </Dialog>
      )}

      <Dialog open={driveSummaryOpen} onOpenChange={setDriveSummaryOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <HardDrive className="h-5 w-5" />
              Riepilogo: {selectedDriveSummary}
            </DialogTitle>
            <DialogDescription>
              Categorie presenti in questo drive
            </DialogDescription>
          </DialogHeader>
          {(() => {
            const space = driveSpaces.find((s) => s.drive_name === selectedDriveSummary);
            if (!space) return null;
            if (space.error) {
              return (
                <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  Errore lettura spazio: {space.error}
                </div>
              );
            }
            return (
              <div className="rounded border px-3 py-2">
                <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                  <span>Spazio disco ({space.path})</span>
                  <span>{space.percent_used}% usato</span>
                </div>
                <div
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                  style={space.percent_used >= 70 ? { backgroundColor: '#22c55e' } : undefined}
                >
                  <div
                    className="h-full"
                    style={{
                      width: `${space.percent_used}%`,
                      backgroundColor: space.percent_used > 90 ? '#ef4444' : space.percent_used > 70 ? '#f59e0b' : '#22c55e',
                    }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
                  <span>Usato: {formatBytes(space.used)}</span>
                  <span>Libero: {formatBytes(space.free)}</span>
                  <span>Totale: {formatBytes(space.total)}</span>
                </div>
              </div>
            );
          })()}
          <div className="space-y-2 py-2">
            {driveSummaryData.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessuna categoria trovata per questo drive.</p>
            )}
            {driveSummaryData.map(([cat, count]) => {
              const total = driveSummaryData.reduce((s, [, c]) => s + c, 0);
              return (
                <div key={cat} className="flex items-center justify-between rounded border px-3 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[cat] || '#e5e7eb' }} />
                    <span className="text-sm font-medium">{cat}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 w-24 overflow-hidden rounded-full bg-muted">
                      <div className="h-full bg-primary" style={{ width: `${Math.round((count / total) * 100)}%` }} />
                    </div>
                    <span className="text-sm font-bold tabular-nums">{count}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setActiveDrive(selectedDriveSummary);
                setDriveSummaryOpen(false);
              }}
            >
              Filtra per questo drive
            </Button>
            <Button variant="secondary" onClick={() => setDriveSummaryOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={categorySummaryOpen} onOpenChange={setCategorySummaryOpen}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" />
              Riepilogo: {selectedCategorySummary}
            </DialogTitle>
            <DialogDescription>
              Drive in cui è presente questa categoria
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {categorySummaryData.length === 0 && (
              <p className="text-sm text-muted-foreground">Nessun drive trovato per questa categoria.</p>
            )}
            {categorySummaryData.map(([drv, count]) => (
                <div key={drv} className="rounded border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HardDrive className="h-3 w-3 text-muted-foreground" />
                      <span className="text-sm font-medium">{drv}</span>
                    </div>
                    <span className="text-sm font-bold tabular-nums">{count}</span>
                  </div>
                  {(() => {
                    const space = driveSpaces.find((s) => s.drive_name === drv);
                    if (!space || space.error) return null;
                    return (
                      <div className="mt-1">
                        <div className="mb-0.5 flex items-center justify-between text-[10px] text-muted-foreground">
                          <span>Spazio: {formatBytes(space.used)} / {formatBytes(space.total)}</span>
                          <span>{space.percent_used}% usato</span>
                        </div>
                        <div
                          className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                          style={space.percent_used >= 70 ? { backgroundColor: '#22c55e' } : undefined}
                        >
                          <div
                            className="h-full"
                            style={{
                              width: `${space.percent_used}%`,
                              backgroundColor: space.percent_used > 90 ? '#ef4444' : space.percent_used > 70 ? '#f59e0b' : '#22c55e',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>
            ))}
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setActiveCategoria(selectedCategorySummary);
                setCategorySummaryOpen(false);
              }}
            >
              Filtra per questa categoria
            </Button>
            <Button variant="secondary" onClick={() => setCategorySummaryOpen(false)}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {confirmHideCategory && (
        <Dialog open={!!confirmHideCategory} onOpenChange={() => setConfirmHideCategory(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Nascondi categoria</DialogTitle>
              <DialogDescription>
                Hai scelto di nascondere la categoria <strong>{confirmHideCategory}</strong> dal catalogo pubblico.
                <br /><br />
                Vuoi procedere?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => setConfirmHideCategory(null)}>
                Annulla
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  const next = [...hiddenCategories, confirmHideCategory];
                  saveHiddenCategories(next);
                  setConfirmHideCategory(null);
                }}
              >
                Sì, nascondi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          className="fixed bottom-6 right-6 z-50 h-12 w-12 rounded-full shadow-lg"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          <ArrowUp className="h-5 w-5" />
        </Button>
      )}

      {sessionClosed && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-black/80 text-white">
          <Power className="h-12 w-12 text-red-500" />
          <h2 className="text-2xl font-bold">Sessione chiusa</h2>
          <p className="text-center text-sm text-gray-300">
            Backend e frontend sono stati arrestati.
            <br />
            Puoi chiudere questa scheda.
          </p>
        </div>
      )}

      {/* Pop-up annunci per utenti */}
      {!isAdmin && announcementOpen && currentAnnouncement && (
        <Dialog open={announcementOpen} onOpenChange={setAnnouncementOpen}>
          <DialogContent className="!w-[90%] max-w-[360px] p-0 overflow-hidden border-0 shadow-2xl bg-[#0f172a]">
            <div className="bg-[#0f172a]">
              <div
                className="flex flex-col items-center pt-6 pb-4 px-6"
                style={{ background: "linear-gradient(90deg, #0f172a 0%, #1e293b 100%)" }}
              >
                <div className="h-16 w-16 rounded-full bg-[#1e293b] border border-[#334155] flex items-center justify-center mb-3 shadow-lg">
                  <Megaphone className="h-8 w-8 text-[#3b82f6]" />
                </div>
                <DialogTitle className="text-center text-xl font-bold text-[#f1f5f9]">
                  {currentAnnouncement.titolo}
                </DialogTitle>
              </div>
              <div className="px-6 pb-2">
                <div className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-wrap bg-[#1e293b] rounded-lg p-4 border border-[#334155]">
                  {currentAnnouncement.messaggio}
                </div>
              </div>
              <div className="p-4 bg-[#0f172a]">
                <Button
                  className="w-full bg-[#3b82f6] hover:bg-blue-700 text-white font-semibold shadow-md transition-colors"
                  onClick={() => {
                    let seen = [];
                    try {
                      const raw = localStorage.getItem("seenAnnouncementIds");
                      if (raw) seen = JSON.parse(raw);
                    } catch {}
                    if (!seen.includes(currentAnnouncement.id)) {
                      seen.push(currentAnnouncement.id);
                      localStorage.setItem("seenAnnouncementIds", JSON.stringify(seen));
                    }
                    setAnnouncementOpen(false);
                    setCurrentAnnouncement(null);
                  }}
                >
                  Ho capito
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Gestione annunci admin */}
      {isAdmin && adminAnnouncementOpen && (
        <Dialog open={adminAnnouncementOpen} onOpenChange={setAdminAnnouncementOpen}>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>Gestione Annunci</DialogTitle>
              <DialogDescription>Crea o modifica gli annunci visibili agli utenti all&apos;avvio.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Titolo</Label>
                <Input
                  value={announcementForm.titolo}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, titolo: e.target.value })}
                  placeholder="Titolo dell'annuncio"
                />
              </div>
              <div className="grid gap-2">
                <Label>Messaggio</Label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={announcementForm.messaggio}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, messaggio: e.target.value })}
                  placeholder="Testo dell'annuncio..."
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={announcementForm.attivo}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, attivo: e.target.checked })}
                  />
                  Attivo
                </label>
                <div className="grid gap-1">
                  <Label className="text-xs">Scadenza (opzionale)</Label>
                  <Input
                    type="datetime-local"
                    value={announcementForm.scadenza}
                    onChange={(e) => setAnnouncementForm({ ...announcementForm, scadenza: e.target.value })}
                  />
                </div>
              </div>
              <Button
                onClick={async () => {
                  try {
                    await api.post("/api/admin/annunci", { ...announcementForm, scadenza: announcementForm.scadenza || null });
                    toast.success("Annuncio creato");
                    setAnnouncementForm({ titolo: "", messaggio: "", attivo: true, scadenza: "" });
                    fetchAdminAnnouncements();
                  } catch (err) {
                    toast.error("Errore nella creazione");
                  }
                }}
                disabled={!announcementForm.titolo || !announcementForm.messaggio}
              >
                <Plus className="mr-2 h-4 w-4" /> Crea annuncio
              </Button>

              <div className="mt-2 space-y-2">
                {adminAnnouncements.map((ann) => (
                  <div key={ann.id} className="flex items-start justify-between rounded border p-2 text-sm">
                    <div className="flex-1">
                      <div className="font-semibold">{ann.titolo}</div>
                      <div className="text-muted-foreground line-clamp-2">{ann.messaggio}</div>
                      <div className="mt-1 text-[10px] text-muted-foreground">
                        {ann.attivo ? "🟢 Attivo" : "⚫ Inattivo"}
                        {ann.scadenza ? ` · Scade ${new Date(ann.scadenza).toLocaleString("it-IT")}` : ""}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await api.put(`/api/admin/annunci/${ann.id}`, { ...ann, attivo: !ann.attivo, scadenza: ann.scadenza || null });
                            fetchAdminAnnouncements();
                          } catch {
                            toast.error("Errore");
                          }
                        }}
                      >
                        {ann.attivo ? "Disattiva" : "Attiva"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500"
                        onClick={async () => {
                          if (!window.confirm("Eliminare l'annuncio?")) return;
                          try {
                            await api.delete(`/api/admin/annunci/${ann.id}`);
                            fetchAdminAnnouncements();
                          } catch {
                            toast.error("Errore eliminazione");
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {adminAnnouncements.length === 0 && (
                  <div className="text-sm text-muted-foreground">Nessun annuncio creato.</div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

    </div>
  );
}

function isNewEpisodesActive(film) {
  if (!film.has_new_episodes || !film.new_episodes_date) return false;
  const added = new Date(film.new_episodes_date);
  const now = new Date();
  const diffHours = (now - added) / (1000 * 60 * 60);
  return diffHours <= 48;
}

function getResolutionStyle(res) {
  switch (res) {
    case "720": return { bg: "#3b82f6", label: "720" };
    case "1080": return { bg: "#22c55e", label: "1080" };
    case "4K": return { bg: "#eab308", label: "4K" };
    case "SD": return { bg: "#9ca3af", label: "SD" };
    default: return null;
  }
}

function FilmListItem({ film, onClick, latestDate }) {
  const isLatest = latestDate && film.data_aggiunta?.startsWith(latestDate);
  return (
    <div
      onClick={() => onClick(film)}
      className="flex cursor-pointer items-center gap-3 rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-accent"
    >
      {film.copertina_url && (
        <img
          src={film.copertina_url.startsWith("http") ? film.copertina_url : film.copertina_url}
          alt={film.titolo}
          className="h-14 w-10 shrink-0 rounded object-cover"
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="truncate text-sm font-semibold leading-tight">{film.titolo}</h3>
          {film.anno && <span className="shrink-0 text-xs text-muted-foreground">({film.anno})</span>}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {film.regista && <span className="truncate">{film.regista}</span>}
          {film.regista && film.genere && <span>·</span>}
          {film.genere && <span className="truncate">{film.genere}</span>}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {isNewEpisodesActive(film) && (
          <Badge className="bg-orange-500 text-white text-[9px] border-0">
            NUOVI EPISODI
          </Badge>
        )}
        {isLatest && (
          <Badge className="bg-green-600 text-white text-[9px] border-0">
            NEW ENTRY {latestDate.split('-').reverse().join('/')}
          </Badge>
        )}
        {film.categoria && (
          <Badge
            variant="outline"
            className="inline-block text-[10px] text-black"
            style={{ backgroundColor: CATEGORY_COLORS[film.categoria] || "#f3f4f6", borderColor: CATEGORY_COLORS[film.categoria] || "#d1d5db" }}
          >
            {film.categoria}
          </Badge>
        )}
        {film.voto_personale != null && (
          <span className="flex items-center gap-1 text-xs font-bold text-yellow-500">
            <Star className="h-3 w-3 fill-yellow-500" />
            {film.voto_personale}
          </span>
        )}
        {film.risoluzione && getResolutionStyle(film.risoluzione) && (
          <span
            className="inline-flex items-center rounded px-1.5 py-[1px] text-[9px] font-bold text-white"
            style={{ backgroundColor: getResolutionStyle(film.risoluzione).bg }}
          >
            {getResolutionStyle(film.risoluzione).label}
          </span>
        )}
      </div>
    </div>
  );
}

function FilmCard({ film, readonly, onEdit, onDelete, onClick, compact, isHiddenCategory, latestDate }) {
  const [imgError, setImgError] = useState(false);
  const [hovered, setHovered] = useState(false);
  const isLatest = latestDate && film.data_aggiunta?.startsWith(latestDate);

  useEffect(() => {
    // Resetta lo stato errore quando cambia l'URL, senza precaricare
    setImgError(!film.copertina_url);
  }, [film.copertina_url]);

  return (
    <Card
      className="group relative flex flex-col overflow-hidden transition-shadow hover:shadow-lg cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(film)}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden bg-muted">
        {isNewEpisodesActive(film) && (
          compact ? (
            <div className="absolute top-1 left-1 z-30 flex flex-col items-center rounded bg-orange-500 px-1 py-[1px] text-[7px] font-bold text-white leading-none shadow">
              <span>NUOVI</span>
              <span>EPISODI</span>
            </div>
          ) : (
            <div className="absolute top-1.5 left-1.5 z-30 flex flex-col items-center rounded bg-orange-500 px-1 py-[1px] text-[8px] font-bold text-white leading-none shadow">
              <span>NUOVI</span>
              <span>EPISODI</span>
            </div>
          )
        )}
        {isLatest && (
          compact ? (
            <div className="absolute top-1 right-1 z-30 flex flex-col items-center rounded bg-green-600 px-1 py-[1px] text-[7px] font-bold text-white leading-none shadow">
              <span>NEW</span>
              <span>ENTRY</span>
              <span>{latestDate.split('-').reverse().join('/')}</span>
            </div>
          ) : (
            <div className="absolute top-1.5 right-1.5 z-30 flex flex-col items-center rounded bg-green-600 px-1 py-[1px] text-[8px] font-bold text-white leading-none shadow">
              <span>NEW</span>
              <span>ENTRY</span>
              <span>{latestDate.split('-').reverse().join('/')}</span>
            </div>
          )
        )}
        {film.copertina_url && !imgError ? (
          <img
            src={resolveImageUrl(film.copertina_url)}
            alt={film.titolo}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-muted-foreground">
            <Film className="h-12 w-12" />
            <span className="text-xs">Nessuna copertina</span>
          </div>
        )}

        {/* Overlay click-catcher per aprire il dettaglio */}
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onClick(film); }}
        />

        {!readonly && hovered && (
          <div className="absolute inset-0 z-20 flex items-start justify-end gap-2 bg-gradient-to-b from-black/60 to-transparent p-3 pointer-events-none">
            <Button
              size="icon"
              variant="secondary"
              className="h-8 w-8 pointer-events-auto"
              onClick={(e) => { e.stopPropagation(); onEdit(film); }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="destructive"
              className="h-8 w-8 pointer-events-auto"
              onClick={(e) => { e.stopPropagation(); onDelete(film.id); }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}

        {film.voto_personale != null && (
          <div className="absolute bottom-2 left-2 z-20 flex items-center gap-1 rounded bg-black/70 px-2 py-1 text-xs font-bold text-yellow-400 pointer-events-none">
            <Star className="h-3 w-3 fill-yellow-400" />
            {film.voto_personale}
          </div>
        )}
      </div>

      <CardHeader className={compact ? "p-2 pb-1" : "p-4 pb-2"}>
        <h3 className={compact ? "line-clamp-1 text-sm font-semibold leading-tight" : "line-clamp-1 text-base font-semibold leading-tight"}>
          {film.titolo}
        </h3>
        <div className={compact ? "mt-0.5 flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground" : "mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground"}>
          {film.anno && (
            <span className="flex items-center gap-1">
              <Calendar className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} /> {film.anno}
            </span>
          )}
          {film.regista && (
            <span className="flex items-center gap-1">
              <User className={compact ? "h-2.5 w-2.5" : "h-3 w-3"} /> {film.regista}
            </span>
          )}
          {film.risoluzione && getResolutionStyle(film.risoluzione) && (
            <span
              className="inline-flex items-center rounded px-1 py-[1px] text-[9px] font-bold text-white"
              style={{ backgroundColor: getResolutionStyle(film.risoluzione).bg }}
            >
              {getResolutionStyle(film.risoluzione).label}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className={compact ? "flex flex-1 flex-col px-2 pb-2 pt-0" : "flex flex-1 flex-col px-4 pb-4 pt-0"}>
        {film.genere && (
          <div className={compact ? "mb-1 flex flex-wrap gap-1" : "mb-2 flex flex-wrap gap-1"}>
            {film.genere.split(",").map((g) => (
              <Badge key={g} variant="secondary" className="text-[10px]">
                {g.trim()}
              </Badge>
            ))}
          </div>
        )}
        {film.categoria && (
          <Badge
            variant="outline"
            className={compact ? "mb-1 block w-full truncate text-center text-[10px] text-black" : "mb-2 block w-full truncate text-center text-[10px] text-black"}
            style={{ backgroundColor: CATEGORY_COLORS[film.categoria] || "#f3f4f6", borderColor: CATEGORY_COLORS[film.categoria] || "#d1d5db" }}
          >
            {film.categoria}
          </Badge>
        )}
        {/* La descrizione è visibile solo nel popup di dettaglio */}
        {!readonly && film.percorso_file && (
          <div className={compact ? "mt-1.5 flex items-center gap-1 text-[10px] text-muted-foreground" : "mt-3 flex items-center gap-1 text-xs text-muted-foreground"}>
            <FolderOpen className={compact ? "h-2.5 w-2.5 shrink-0" : "h-3 w-3 shrink-0"} />
            <span className="truncate" title={film.percorso_file}>
              {film.percorso_file}
            </span>
          </div>
        )}
        {isHiddenCategory && (
          <div
            className={compact ? "mt-1 flex items-center gap-1 text-[10px] text-red-500" : "mt-2 flex items-center gap-1 text-xs text-red-500"}
            title="Categoria nascosta nel catalogo pubblico"
          >
            <EyeOff className={compact ? "h-3 w-3" : "h-4 w-4"} />
            <span>Nascosto</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
